import {
  SourceSpecification,
  LayerSpecification,
  LngLatLike,
  ExpressionSpecification,
  ExpressionInputType,
  FilterSpecification,
  DataDrivenPropertyValueSpecification,
} from 'maplibre-gl';

import { centroid, center, polygon, points } from '@turf/turf';
import * as Utils from './utils';
import { Map } from './Map';

import {
  layerObj,
  layerSource,
  property,
  colorType,
  // layerType,
  paintObject,
  // expressionType,
  subLayerType,
  layerSourceType,
  Source,
  layerSettings
} from './Types';

// import expression from "./Expression";
import { Layer, LAYER_MIN_ZOOM, LAYER_MAX_ZOOM } from './Layer';
import {
  featureGeometryForDraw,
  GEOBOX_API_GEOMETRY_SRID,
  prepareSketchFeatureForCreate,
} from './Controls/featureCreateGeometry';

function resolveDrawModeForLayer(
  layerType: string | undefined,
  subtype?: string
): string {
  const lt = String(layerType ?? '');
  switch (lt) {
    case 'Polygon': {
      switch (subtype) {
        case 'rectangle': return 'draw_rectangle';
        case 'circle': return 'draw_circle';
        case 'freehand': return 'draw_polygon_freehand';
        case 'polygon':
        default: return 'draw_polygon';
      }
    }
    case 'Polyline':
    case 'Line': {
      switch (subtype) {
        case 'bezier': return 'draw_bezier';
        case 'line':
        default: return 'draw_line_string';
      }
    }
    default:
      return 'draw_point';
  }
}
// import layout from './const/layout.json';
// import defaults from './const/default.json';

// import { PointVectorLayer } from './PointVectorLayer';
// import { LineVectorLayer } from './LineVectorLayer';
// import { PolygonVectorLayer } from './PolygonVectorLayer';

export class FeatureLayer extends Layer {
  public selectionSet = new Set();

  public highlightSet = new Set();

  public editIds: number[] = [];

  public layer_settings: layerSettings = <layerSettings>{};

  private _resolveDrawFeatureId(serverId: number | string): string | undefined {
    const drawTools = this.mapObj.drawTools;
    if (!drawTools) return undefined;
    const sid = String(serverId);
    try {
      if (drawTools.get(sid)) return sid;
    } catch {
      /* ignore */
    }
    const all = drawTools.getAll?.()?.features ?? [];
    const match = all.find(
      (f: any) =>
        String(f.id) === sid ||
        String(f.properties?.id) === sid ||
        String(f.properties?.fid) === sid
    );
    if (match?.id != null) return String(match.id);
    if (all.length === 1) return String(all[0].id);
    return undefined;
  }

  private _isFeatureBeingEdited(id: number | string): boolean {
    const sid = String(id);
    return this.editIds.some((eid) => String(eid) === sid);
  }

  private _removeEditId(id: number | string) {
    const sid = String(id);
    this.editIds = this.editIds.filter((eid) => String(eid) !== sid);
  }

  private _deleteDrawFeature(serverId: number | string) {
    const drawTools = this.mapObj.drawTools;
    if (!drawTools) return;
    const drawDeleteId = this._resolveDrawFeatureId(serverId);
    try {
      if (drawDeleteId) {
        drawTools.delete(drawDeleteId);
      } else {
        drawTools.deleteAll();
      }
    } catch {
      try {
        drawTools.deleteAll();
      } catch {
        /* ignore */
      }
    }
    if (drawTools.getAll?.().features?.length === 0) {
      this.mapObj.disableDraw();
    }
  }

  constructor(
    layerSource: layerSource,
    mapObj: Map,
    sourceLayerId?: string,
    layerId?: string,
    sourceId?: string
  ) {
    super(layerSource, mapObj, sourceLayerId, layerId, sourceId);

    this.source = layerSource;
    this.mapObj = mapObj;

    this.prefix = layerSource.layer_source_type;

    this.sourceId = sourceId || `geobox://${this.prefix}/${this.source.id}`;

    this.layerId = layerId || `${this.prefix}/${this.source.name}`;
 
    this.sourceLayer = sourceLayerId || this.source.name;

    if (sourceId && sourceId.includes('geobox://tileset/')) {
      this.sourceType = 'tileset';
    } else {
      this.sourceType = <layerSourceType>this.prefix;
    }

    this.layerType = this.source.layer_type;

    this.colorType = 'line-color';

    this.getAllFields();

    this.getPublishedFields();

    this.getLayerSettings();
  }

  getLayerSettings = async () =>{
    try {
      return this.layer_settings = await this.source.getSettings();

    } catch (error) {
      
    }
  }
  

  getAllFields = async () => {
    try {
      this.allFields = await this.source.getFields();

      if (this.layerType === 'Polyline') {
        this.allFields.push({
          name: 'geom_length',
          display_name: '[Length]',
          domain: undefined,
          datatype: 'Float',
          hyperlink: false
        });
      } else if (this.layerType === 'Polygon') {
        this.allFields.push({
          name: 'geom_length',
          display_name: '[Length]',
          domain: undefined,
          datatype: 'Float',
          hyperlink: false
        });
        this.allFields.push({
          name: 'geom_area',
          display_name: '[Area]',
          domain: undefined,
          datatype: 'Float',
          hyperlink: false
        });
      }
      return this.allFields;
    } catch (error) {}
  };

  getPublishedFields = async () => {
    try {
      let layerInfo;
      // await this.getAllFields();
      layerInfo = await this.source.getTilejson();

      this.publishedFields = [];
      for (const key in layerInfo.vector_layers[0].fields) {
        this.allFields.forEach((field) => {
          if (field.name === key) {
            this.publishedFields.push(field);
          }
        });
      }
    } catch (error) {}
  };

  toMaplibreLayer = (opt: LayerSpecification) => {
    let layerObj: layerObj;

    let { layerId } = this;
    layerId = layerId.split('#')[0]

    let layerRepeatNumber = 0;

    let layerList = this.mapObj.getLayersOrder()

    if (layerList.includes(`${layerId}`)) {
      layerRepeatNumber += 1;
    }

    for (const layer of this.mapObj.getStyle().layers) {
      if (layerList.includes(`${layerId}#${layerRepeatNumber}`)) {
        layerRepeatNumber += 1;
      }
    }
    if(layerRepeatNumber > 0) layerId = `${layerId}#${layerRepeatNumber}`;

    this.layerId = layerId;

    switch (this.source.layer_type) {
      case 'Polyline':
        if (opt.type === 'fill') {
          layerObj = {
            id: layerId,
            type: 'fill',
            source: this.sourceId,
            'source-layer': this.sourceLayer,
            layout: opt.layout || {},
            paint: this.layerPaintObj('fill', opt),
          };
        } else {
          layerObj = {
            id: layerId,
            type: 'line',
            source: this.sourceId,
            'source-layer': this.sourceLayer,
            layout: opt.layout || {
              'line-join': 'round',
              'line-cap': 'round',
            },
            paint: this.layerPaintObj('line', opt),
          };
        }

        break;
      case 'Polygon':
        if (opt.type === 'line') {
          layerObj = {
            id: layerId,
            type: 'line',
            source: this.sourceId,
            'source-layer': this.sourceLayer,
            layout: opt.layout || {
              'line-join': 'round',
              'line-cap': 'round',
            },
            paint: this.layerPaintObj('line', opt),
          };
        } else {
          layerObj = {
            id: layerId,
            type: 'fill',
            source: this.sourceId,
            'source-layer': this.sourceLayer,
            layout: opt.layout || {},
            paint: this.layerPaintObj('fill', opt),
          };
        }
        break;
      case 'Point':
      case 'Multipoint':
        if (opt.type === 'symbol') {
          layerObj = {
            id: layerId,
            type: 'symbol',
            source: this.sourceId,
            'source-layer': this.sourceLayer,
            layout: opt.layout || {},
            paint: this.layerPaintObj('symbol', opt),
          };
        } else {
          layerObj = {
            id: layerId,
            type: 'circle',
            source: this.sourceId,
            'source-layer': this.sourceLayer,
            layout: opt.layout || {},
            paint: this.layerPaintObj('circle', opt),
          };
        }
        break;

      default:
        layerObj = {
          id: layerId,
          type: 'circle',
          source: this.sourceId,
          'source-layer': this.sourceLayer,
          layout: opt.layout || {},
          paint: this.layerPaintObj('circle', opt),
        };
        break;
    }

    if (this.sourceId.includes('geobox://tileset/')) {
      layerObj['source-layer-id'] = this.source.id;
    }
    return layerObj;
  };

  layerPaintObj = (
    layerType: string,
    opt: LayerSpecification = <LayerSpecification>{}
  ) => {
    let colorType: colorType = 'line-color';
    let layerPaintObj: paintObject = {};
    const selectColor: string =
      this.mapObj.mapSettings.general_settings.selection_color; //  opt["selection_color"] ||

    const hoverColor: string =
      this.mapObj.mapSettings.general_settings.highlight_color; //   opt["highlight_color"] ||
    if (
      layerType === 'heatmap' ||
      layerType === 'hillshade' ||
      layerType === 'raster' ||
      layerType === 'background'
    ) {
      return {};
    }
    if (
      layerType === 'line' ||
      layerType === 'fill' ||
      layerType === 'circle'
    ) {
      colorType = `${layerType}-color`;
    } else if (layerType === 'symbol') {
      colorType = 'icon-color';
    }

    this.colorType = colorType;

    const paintObject = <paintObject>opt.paint;

    if (opt.paint) {
      if (paintObject[colorType]) {
        const [type, expression] = this.makeExpression(paintObject[colorType]);

        const newExpression = <any[]>expression;

        let fillHoverColor: string | string[];
        if (type === 'interpolate') {
          for (let i = 4; i < newExpression.length; i += 2) {
            if (layerType === 'fill' || layerType === 'fill-extrusion') {
              fillHoverColor = Utils.hexToRgba(hoverColor);
              if (
                !(
                  Utils.isArray(newExpression[i]) ||
                  Utils.isObject(newExpression[i])
                )
              ) {
                fillHoverColor = Utils.hexToRgba(newExpression[i]);
              }
              fillHoverColor = fillHoverColor.replace(/\s/g, '');
              if (fillHoverColor.includes('rgba')) {
                const SplitedColor = fillHoverColor
                  .slice(5, fillHoverColor.length - 1)
                  .split(',');
                fillHoverColor = `rgba(${Number(SplitedColor[0])},${Number(
                  SplitedColor[1]
                )},${Number(SplitedColor[2])},${
                  Number(SplitedColor[3]) - 0.1
                })`;
              } else if (fillHoverColor.includes('rgb')) {
                const SplitedColor = fillHoverColor
                  .slice(4, fillHoverColor.length - 1)
                  .split(',');
                fillHoverColor = `rgba(${Number(SplitedColor[0])},${Number(
                  SplitedColor[1]
                )},${Number(SplitedColor[2])},0.5)`;
              }

              newExpression[i] = [
                'case',
                ['boolean', ['feature-state', 'select'], false],
                selectColor,
                ['boolean', ['feature-state', 'highlight'], false],
                fillHoverColor,
                newExpression[i],
              ];
            } else {
              newExpression[i] = [
                'case',
                ['boolean', ['feature-state', 'select'], false],
                selectColor,
                ['boolean', ['feature-state', 'highlight'], false],
                hoverColor,
                newExpression[i],
              ];
            }
          }

          paintObject[colorType] = <
            DataDrivenPropertyValueSpecification<string>
          >newExpression;
          
        } else {
          let selfColor = paintObject[colorType];
          if (type === 'identity') {
            selfColor = <DataDrivenPropertyValueSpecification<string>>(
              newExpression
            );
          }

          if (layerType === 'fill' || layerType === 'fill-extrusion') {
            fillHoverColor = Utils.hexToRgba(hoverColor);
            if (!(Utils.isArray(selfColor) || Utils.isObject(selfColor))) {
              fillHoverColor = Utils.hexToRgba(<string>selfColor);
              fillHoverColor = fillHoverColor.replace(/\s/g, '');
              if (fillHoverColor.includes('rgba')) {
                const SplitedColor = fillHoverColor
                  .slice(5, fillHoverColor.length - 1)
                  .split(',');
                fillHoverColor = `rgba(${Number(SplitedColor[0])},${Number(
                  SplitedColor[1]
                )},${Number(SplitedColor[2])},${
                  Number(SplitedColor[3]) - 0.1
                })`;
              } else if (fillHoverColor.includes('rgb')) {
                const SplitedColor = fillHoverColor
                  .slice(4, fillHoverColor.length - 1)
                  .split(',');
                fillHoverColor = `rgba(${Number(SplitedColor[0])},${Number(
                  SplitedColor[1]
                )},${Number(SplitedColor[2])},0.5)`;
              }
            } else if ((<string[]>selfColor)[0] === 'match') {
              fillHoverColor = [...(<string[]>selfColor)];
              if (fillHoverColor.length > 1) {
                for (
                  let index = 3;
                  index < fillHoverColor.length - 1;
                  index += 2
                ) {
                  let color = Utils.hexToRgba(fillHoverColor[index]);
                  color = color.replace(/\s/g, '');
                  if (color.includes('rgba')) {
                    const SplitedColor = color
                      .slice(5, color.length - 1)
                      .split(',');
                    color = `rgba(${Number(SplitedColor[0])},${Number(
                      SplitedColor[1]
                    )},${Number(SplitedColor[2])},${
                      Number(SplitedColor[3]) - 0.1
                    })`;
                  } else if (color.includes('rgb')) {
                    const SplitedColor = color
                      .slice(4, color.length - 1)
                      .split(',');
                    color = `rgba(${Number(SplitedColor[0])},${Number(
                      SplitedColor[1]
                    )},${Number(SplitedColor[2])},0.5)`;
                  }
                  fillHoverColor[index] = color;
                }

                let color = Utils.hexToRgba(
                  fillHoverColor[fillHoverColor.length - 1]
                );
                color = color.replace(/\s/g, '');
                if (color.includes('rgba')) {
                  const SplitedColor = color
                    .slice(5, color.length - 1)
                    .split(',');
                  color = `rgba(${Number(SplitedColor[0])},${Number(
                    SplitedColor[1]
                  )},${Number(SplitedColor[2])},${
                    Number(SplitedColor[3]) - 0.1
                  })`;
                } else if (color.includes('rgb')) {
                  const SplitedColor = color
                    .slice(4, color.length - 1)
                    .split(',');
                  color = `rgba(${Number(SplitedColor[0])},${Number(
                    SplitedColor[1]
                  )},${Number(SplitedColor[2])},0.5)`;
                }

                fillHoverColor[fillHoverColor.length - 1] = color;
              }
            }

            paintObject[colorType] = [
              'case',
              ['boolean', ['feature-state', 'select'], false],
              selectColor,
              ['boolean', ['feature-state', 'highlight'], false],
              <ExpressionInputType>fillHoverColor,
              <ExpressionInputType>selfColor,
            ];
          } else {
            paintObject[colorType] = [
              'case',
              ['boolean', ['feature-state', 'select'], false],
              selectColor,
              ['boolean', ['feature-state', 'highlight'], false],
              hoverColor,
              <ExpressionInputType>selfColor,
            ];
          }
        }
      } else if (layerType === 'fill' || layerType === 'fill-extrusion') {
        let fillHoverColor = Utils.hexToRgba('rgba(0,0,0,1)');
        if (fillHoverColor.includes('rgba')) {
          const SplitedColor = fillHoverColor
            .slice(5, fillHoverColor.length - 1)
            .split(',');
          fillHoverColor = `rgba(${Number(SplitedColor[0])},${Number(
            SplitedColor[1]
          )},${Number(SplitedColor[2])},${Number(SplitedColor[3]) - 0.2})`;
        } else if (fillHoverColor.includes('rgb')) {
          const SplitedColor = fillHoverColor
            .slice(4, fillHoverColor.length - 1)
            .split(',');
          fillHoverColor = `rgba(${Number(SplitedColor[0])},${Number(
            SplitedColor[1]
          )},${Number(SplitedColor[2])},0.5)`;
        }
        paintObject[colorType] = [
          'case',
          ['boolean', ['feature-state', 'select'], false],
          selectColor,
          ['boolean', ['feature-state', 'highlight'], false],
          fillHoverColor,
          'black',
        ];
      } else {
        paintObject[colorType] = [
          'case',
          ['boolean', ['feature-state', 'select'], false],
          selectColor,
          ['boolean', ['feature-state', 'highlight'], false],
          hoverColor,
          'black',
        ];
      }

      layerPaintObj = <paintObject>opt.paint;
    } else if (layerType === 'fill' || layerType === 'fill-extrusion') {
      let fillHoverColor = Utils.hexToRgba('rgba(0,0,0,1)');
      if (fillHoverColor.includes('rgba')) {
        const SplitedColor = fillHoverColor
          .slice(5, fillHoverColor.length - 1)
          .split(',');
        fillHoverColor = `rgba(${Number(SplitedColor[0])},${Number(
          SplitedColor[1]
        )},${Number(SplitedColor[2])},${Number(SplitedColor[3]) - 0.2})`;
      } else if (fillHoverColor.includes('rgb')) {
        const SplitedColor = fillHoverColor
          .slice(4, fillHoverColor.length - 1)
          .split(',');
        fillHoverColor = `rgba(${Number(SplitedColor[0])},${Number(
          SplitedColor[1]
        )},${Number(SplitedColor[2])},0.5)`;
      }
      layerPaintObj[colorType] = [
        'case',
        ['boolean', ['feature-state', 'select'], false],
        selectColor,
        ['boolean', ['feature-state', 'highlight'], false],
        fillHoverColor,
        'black',
      ];
    } else {
      layerPaintObj[colorType] = [
        'case',
        ['boolean', ['feature-state', 'select'], false],
        selectColor,
        ['boolean', ['feature-state', 'highlight'], false],
        hoverColor,
        'black',
      ];
    }

    if (layerType === 'fill') {
      if (opt.paint && paintObject['fill-outline-color']) {
        const [type, expression] = this.makeExpression(
          paintObject['fill-outline-color']
        );

        const newExpression = <any[]>expression;

        if (type === 'interpolate') {
          for (let i = 4; i < newExpression.length; i += 2) {
            newExpression[i] = [
              'case',
              ['boolean', ['feature-state', 'highlight'], false],
              hoverColor,
              newExpression[i],
            ];
          }
          paintObject['fill-outline-color'] = <
            DataDrivenPropertyValueSpecification<string>
          >newExpression;
        } else {
          let selfColor = paintObject['fill-outline-color'];
          if (type === 'identity') {
            selfColor = <DataDrivenPropertyValueSpecification<string>>(
              newExpression
            );
          }
          paintObject['fill-outline-color'] = [
            'case',
            ['boolean', ['feature-state', 'highlight'], false],
            hoverColor,
            <ExpressionInputType>selfColor,
          ];
        }
        layerPaintObj = <paintObject>opt.paint;
      } else if (opt.paint) {
        paintObject['fill-outline-color'] = [
          'case',
          ['boolean', ['feature-state', 'highlight'], false],
          hoverColor,
          'black',
        ];
        layerPaintObj = <paintObject>opt.paint;
      } else {
        layerPaintObj['fill-outline-color'] = [
          'case',
          ['boolean', ['feature-state', 'highlight'], false],
          hoverColor,
          'black',
        ];
      }

      if (opt.paint && paintObject['fill-opacity']) {
        const [type, expression] = this.makeExpression(
          paintObject['fill-opacity']
        );
        const newExpression = <any[]>expression;

        if (type === 'interpolate') {
          for (let i = 4; i < newExpression.length; i += 2) {
            newExpression[i] = [
              'case',
              ['boolean', ['feature-state', 'highlight'], false],
              newExpression[i] - 0.1,
              newExpression[i],
            ];
          }
          paintObject['fill-opacity'] = <
            DataDrivenPropertyValueSpecification<number>
          >newExpression;
        } else {
          let selfOpacity = paintObject['fill-opacity'];
          if (type === 'identity') {
            selfOpacity = <DataDrivenPropertyValueSpecification<number>>(
              newExpression
            );
          }
          paintObject['fill-opacity'] = [
            'case',
            ['boolean', ['feature-state', 'highlight'], false],
            <number>selfOpacity - 0.1 || 0.9,
            <ExpressionInputType>selfOpacity,
          ];
        }
        layerPaintObj = <paintObject>opt.paint;
      }
    }

    return layerPaintObj;
  };

  toMaplibreSource = async () => {
    const minzoom = LAYER_MIN_ZOOM;
    const maxzoom = LAYER_MAX_ZOOM;

    const sourceObj: SourceSpecification = {
      type: 'vector',
      minzoom,
      maxzoom,
    };

    let layerInfo;

    if (this.source.layer_type === 'tileset') {
      layerInfo = `${this.source._endpoint}tilejson.json`;
    } else {
      layerInfo = await this.source.getTilejson();
    }
    if (this.source.layer_type === 'tileset') {
      sourceObj.url = layerInfo;
    } else {
      try {
        sourceObj.tiles = layerInfo.tiles;
      } catch (error) {}
    }

    return sourceObj;
  };

  getFeature = async (id: number, skip_geometry:boolean = true) => {
    const features = await this.source.getFeatures({
      f: 'json',
      q: `id=${id}`,
      out_srid: 4326,
      skip_geometry
    });

    if (features.features.length === 0) {
      console.log('Feature not found');
      return;
    }
    
    return features.features[0];
  };

  zoomToFeature = async (id: number, options: object = { maxZoom: 22 }) => {
    const features = await this.source.getFeatures({
      f: 'json',
      q: `id=${id}`,
      out_srid: 4326,
      select_fields: ['id'],
    });

    if (features.features.length === 0) {
      console.log('Feature not found');
      return;
    }
    this.mapObj.fitBounds(features.bbox, options);
  };

  panToFeature = async (id: number, options: object = { maxZoom: 22 }) => {
    const features = await this.source.getFeatures({
      f: 'json',
      q: `id=${id}`,
      out_srid: 4326,
      select_fields: ['id'],
    });

    if (features.features.length === 0) {
      console.log('Feature not found');
      return;
    }

    try {
      let featureBBox = features.bbox;
      featureBBox = polygon([
        [
          [featureBBox[0], featureBBox[1]],
          [featureBBox[2], featureBBox[1]],
          [featureBBox[2], featureBBox[3]],
          [featureBBox[0], featureBBox[3]],
          [featureBBox[0], featureBBox[1]],
        ],
      ]);

      const featureCenterPoint = centroid(featureBBox);

      this.mapObj.panTo(
        <LngLatLike>featureCenterPoint.geometry.coordinates,
        options
      );
    } catch (error) {
      throw new Error("Feature not found");
    }
  };

  getFeatureCenter = async (id: number) => {
    const features = await this.source.getFeatures({
      f: 'json',
      q: `id=${id}`,
      out_srid: 4326,
      select_fields: ['id'],
    });

    if (features.features.length === 0) {
      console.log('Feature not found');
      return;
    }
    var featureBBox = features.bbox;
    featureBBox = polygon([
        [
          [featureBBox[0], featureBBox[1]],
          [featureBBox[2], featureBBox[1]],
          [featureBBox[2], featureBBox[3]],
          [featureBBox[0], featureBBox[3]],
          [featureBBox[0], featureBBox[1]],
        ],
      ]);
    
    return centroid(featureBBox).geometry.coordinates;
  };

  changeFeatureStyle = (layer: LayerSpecification) => {
    let colorType: colorType;
    const selectColor =
      this.mapObj.mapSettings.general_settings.selection_color;
    let fillHoverColor: string | string[];
    const hoverColor = this.mapObj.mapSettings.general_settings.highlight_color;
    if (
      layer.type === 'heatmap' ||
      layer.type === 'hillshade' ||
      layer.type === 'raster' ||
      layer.type === 'background'
    ) {
      return layer;
    }

    if (
      layer.type === 'line' ||
      layer.type === 'fill' ||
      layer.type === 'circle'
    ) {
      colorType = `${layer.type}-color`;
    } else if (layer.type === 'symbol') {
      colorType = 'icon-color';
    } else if (layer.type === 'fill-extrusion') {
      colorType = 'fill-extrusion-color';
    } else {
      colorType = 'line-color';
    }
    this.colorType = colorType;

    try {
      let paintObject = <paintObject>layer.paint;

      if (layer.paint && paintObject[colorType]) {
        const [type, expression] = this.makeExpression(paintObject[colorType]);

        const newExpression = <any[]>expression;

        if (type === 'interpolate') {
          for (let i = 4; i < newExpression.length; i += 2) {
            if (layer.type === 'fill' || layer.type === 'fill-extrusion') {
              fillHoverColor = Utils.hexToRgba(hoverColor);
              if (!(Utils.isArray(newExpression[i]) || Utils.isObject(newExpression[i]))) 
                {
                  fillHoverColor = Utils.hexToRgba(newExpression[i]);
                }

              fillHoverColor = fillHoverColor.replace(/\s/g, '');

              if (fillHoverColor.includes('rgba')) {
                const SplitedColor = fillHoverColor.slice(5, fillHoverColor.length - 1).split(',');

                fillHoverColor = `rgba(${Number(SplitedColor[0])},${Number(SplitedColor[1])},${Number(SplitedColor[2])},${Number(SplitedColor[3]) - 0.1})`;
              } else if (fillHoverColor.includes('rgb')) {
                const SplitedColor = fillHoverColor.slice(4, fillHoverColor.length - 1).split(',');

                fillHoverColor = `rgba(${Number(SplitedColor[0])},${Number(SplitedColor[1])},${Number(SplitedColor[2])},0.5)`;

              }

              newExpression[i] = [
                'case',
                ['boolean', ['feature-state', 'select'], false],
                selectColor,
                ['boolean', ['feature-state', 'highlight'], false],
                fillHoverColor,
                newExpression[i],
              ];
            } else {
              newExpression[i] = [
                'case',
                ['boolean', ['feature-state', 'select'], false],
                selectColor,
                ['boolean', ['feature-state', 'highlight'], false],
                hoverColor,
                newExpression[i],
              ];
            }
          }

          paintObject[colorType] = <DataDrivenPropertyValueSpecification<string>>newExpression;
        } else if (type === "literal" && expression[1] && expression[1][0] === "step"){
          for (let i = 2; i < expression[1].length; i += 2) {
            expression[1][i] = [
              "case",
              ["==", ["feature-state", "select"], true],
              selectColor,
              ["==", ["feature-state", "highlight"], true],
              hoverColor,
              expression[1][i],
            ]
          }
          paintObject[colorType] = expression[1];
        } else {
          let selfColor = paintObject[colorType];
          if (type === 'identity') {
            selfColor = <DataDrivenPropertyValueSpecification<string>>(
              newExpression
            );
          }
          if (layer.type === 'fill' || layer.type === 'fill-extrusion') {
            fillHoverColor = Utils.hexToRgba(hoverColor);
            if (!(Utils.isArray(selfColor) || Utils.isObject(selfColor))) {
              fillHoverColor = Utils.hexToRgba(<string>selfColor);
              fillHoverColor = fillHoverColor.replace(/\s/g, '');
              if (fillHoverColor.includes('rgba')) {
                const SplitedColor = fillHoverColor
                  .slice(5, fillHoverColor.length - 1)
                  .split(',');
                fillHoverColor = `rgba(${Number(SplitedColor[0])},${Number(
                  SplitedColor[1]
                )},${Number(SplitedColor[2])},${
                  Number(SplitedColor[3]) - 0.1
                })`;
              } else if (fillHoverColor.includes('rgb')) {
                const SplitedColor = fillHoverColor
                  .slice(4, fillHoverColor.length - 1)
                  .split(',');
                fillHoverColor = `rgba(${Number(SplitedColor[0])},${Number(
                  SplitedColor[1]
                )},${Number(SplitedColor[2])},0.5)`;
              }
            } else if ((<string[]>selfColor)[0] === 'match') {
              fillHoverColor = [...(<string[]>selfColor)];
              if (fillHoverColor.length > 1) {
                for (
                  let index = 3;
                  index < fillHoverColor.length - 1;
                  index += 2
                ) {
                  let color = Utils.hexToRgba(fillHoverColor[index]);
                  color = color.replace(/\s/g, '');
                  if (color.includes('rgba')) {
                    const SplitedColor = color
                      .slice(5, color.length - 1)
                      .split(',');
                    color = `rgba(${Number(SplitedColor[0])},${Number(
                      SplitedColor[1]
                    )},${Number(SplitedColor[2])},${
                      Number(SplitedColor[3]) - 0.1
                    })`;
                  } else if (color.includes('rgb')) {
                    const SplitedColor = color
                      .slice(4, color.length - 1)
                      .split(',');
                    color = `rgba(${Number(SplitedColor[0])},${Number(
                      SplitedColor[1]
                    )},${Number(SplitedColor[2])},0.5)`;
                  }
                  fillHoverColor[index] = color;
                }

                let color = Utils.hexToRgba(
                  fillHoverColor[fillHoverColor.length - 1]
                );
                color = color.replace(/\s/g, '');
                if (color.includes('rgba')) {
                  const SplitedColor = color
                    .slice(5, color.length - 1)
                    .split(',');
                  color = `rgba(${Number(SplitedColor[0])},${Number(
                    SplitedColor[1]
                  )},${Number(SplitedColor[2])},${
                    Number(SplitedColor[3]) - 0.1
                  })`;
                } else if (color.includes('rgb')) {
                  const SplitedColor = color
                    .slice(4, color.length - 1)
                    .split(',');
                  color = `rgba(${Number(SplitedColor[0])},${Number(
                    SplitedColor[1]
                  )},${Number(SplitedColor[2])},0.5)`;
                }

                fillHoverColor[fillHoverColor.length - 1] = color;
              }
            }

            paintObject[colorType] = [
              'case',
              ['boolean', ['feature-state', 'select'], false],
              selectColor,
              ['boolean', ['feature-state', 'highlight'], false],
              <ExpressionInputType>fillHoverColor,
              <ExpressionInputType>selfColor,
            ];
          } else {
            paintObject[colorType] = [
              'case',
              ['boolean', ['feature-state', 'select'], false],
              selectColor,
              ['boolean', ['feature-state', 'highlight'], false],
              hoverColor,
              <ExpressionInputType>selfColor,
            ];
          }
        }
      } else if (layer.paint) {
        if (layer.type === 'fill' || layer.type === 'fill-extrusion') {
          let fillHoverColor = Utils.hexToRgba('rgba(0,0,0,1)');
          if (fillHoverColor.includes('rgba')) {
            const SplitedColor = fillHoverColor
              .slice(5, fillHoverColor.length - 1)
              .split(',');
            fillHoverColor = `rgba(${Number(SplitedColor[0])},${Number(
              SplitedColor[1]
            )},${Number(SplitedColor[2])},${Number(SplitedColor[3]) - 0.1})`;
          } else if (fillHoverColor.includes('rgb')) {
            const SplitedColor = fillHoverColor
              .slice(4, fillHoverColor.length - 1)
              .split(',');
            fillHoverColor = `rgba(${Number(SplitedColor[0])},${Number(
              SplitedColor[1]
            )},${Number(SplitedColor[2])},0.5)`;
          }
          paintObject[colorType] = [
            'case',
            ['boolean', ['feature-state', 'select'], false],
            selectColor,
            ['boolean', ['feature-state', 'highlight'], false],
            fillHoverColor,
            'black',
          ];
        } else {
          paintObject[colorType] = [
            'case',
            ['boolean', ['feature-state', 'select'], false],
            selectColor,
            ['boolean', ['feature-state', 'highlight'], false],
            hoverColor,
            'black',
          ];
        }
      } else {
        layer.paint = {};
        paintObject = {};

        if (layer.type === 'fill' || layer.type === 'fill-extrusion') {
          let fillHoverColor = Utils.hexToRgba('rgba(0,0,0,1)');
          if (fillHoverColor.includes('rgba')) {
            const SplitedColor = fillHoverColor
              .slice(5, fillHoverColor.length - 1)
              .split(',');
            fillHoverColor = `rgba(${Number(SplitedColor[0])},${Number(
              SplitedColor[1]
            )},${Number(SplitedColor[2])},${Number(SplitedColor[3]) - 0.1})`;
          } else if (fillHoverColor.includes('rgb')) {
            const SplitedColor = fillHoverColor
              .slice(4, fillHoverColor.length - 1)
              .split(',');
            fillHoverColor = `rgba(${Number(SplitedColor[0])},${Number(
              SplitedColor[1]
            )},${Number(SplitedColor[2])},0.5)`;
          }
          paintObject[colorType] = [
            'case',
            ['boolean', ['feature-state', 'select'], false],
            selectColor,
            ['boolean', ['feature-state', 'highlight'], false],
            fillHoverColor,
            'black',
          ];
        } else {
          paintObject[colorType] = [
            'case',
            ['boolean', ['feature-state', 'select'], false],
            selectColor,
            ['boolean', ['feature-state', 'highlight'], false],
            hoverColor,
            'black',
          ];
        }
      }

      layer.paint = paintObject;

      if (layer.type === 'fill') {
        if (layer.paint && layer.paint['fill-outline-color']) {
          const [type, expression] = this.makeExpression(
            layer.paint['fill-outline-color']
          );
          const newExpression = <any[]>expression;

          if (type === 'interpolate') {
            for (let i = 4; i < newExpression.length; i += 2) {
              newExpression[i] = [
                'case',
                ['boolean', ['feature-state', 'highlight'], false],
                hoverColor,
                newExpression[i],
              ];
            }
            layer.paint['fill-outline-color'] = <
              DataDrivenPropertyValueSpecification<string>
            >newExpression;
          } else {
            let selfColor = layer.paint['fill-outline-color'];
            if (type === 'identity') {
              selfColor = <DataDrivenPropertyValueSpecification<string>>(
                newExpression
              );
            }
            layer.paint['fill-outline-color'] = [
              'case',
              ['boolean', ['feature-state', 'highlight'], false],
              hoverColor,
              <ExpressionSpecification>selfColor,
            ];
          }
        } else if (layer.paint) {
          layer.paint['fill-outline-color'] = [
            'case',
            ['boolean', ['feature-state', 'highlight'], false],
            hoverColor,
            'rgba(0,0,0,0)',
          ];
        } else {
          layer.paint = {};
          layer.paint['fill-outline-color'] = [
            'case',
            ['boolean', ['feature-state', 'highlight'], false],
            hoverColor,
            'rgba(0,0,0,0)',
          ];
        }

        if (layer.paint && layer.paint['fill-opacity']) {
          const [type, expression] = this.makeExpression(
            layer.paint['fill-opacity']
          );
          const newExpression = <any[]>expression;

          if (type === 'interpolate') {
            for (let i = 4; i < newExpression.length; i += 2) {
              newExpression[i] = [
                'case',
                ['boolean', ['feature-state', 'highlight'], false],
                newExpression[i] - 0.1,
                newExpression[i],
              ];
            }
            layer.paint['fill-opacity'] = <
              DataDrivenPropertyValueSpecification<number>
            >newExpression;
          } else {
            let selfOpacity: number = <number>layer.paint['fill-opacity'];
            if (type === 'identity') {
              selfOpacity = <number>(<unknown>newExpression);
            }
            layer.paint['fill-opacity'] = [
              'case',
              ['boolean', ['feature-state', 'highlight'], false],
              selfOpacity - 0.1 || 0.9,
              selfOpacity,
            ];
          }
        }
      }
    } catch (error) {
      // throw new Error(String(error));
    }

    return layer;
  };

  highlight = (ids: number[] | number) => {
    this.clearHighlight();
    if (Utils.isArray(ids)) {
      for (const id of <number[]>ids) {
        if (this.mapObj.getSource(this.sourceId)) {
          if (this.mapObj.getSource(this.sourceId)?.type === 'geojson') {
            this.mapObj.setFeatureState(
              { source: this.sourceId, id },
              { highlight: true }
            );
          } else {
            this.mapObj.setFeatureState(
              {
                source: this.sourceId,
                sourceLayer: this.sourceLayer,
                id,
              },
              { highlight: true }
            );
          }
        }

        this.highlightSet.add(id);
      }
    } else if (!isNaN(<number>ids)) {
      const id = Number(ids);

      if (this.mapObj.getSource(this.sourceId)) {
        if (this.mapObj.getSource(this.sourceId)?.type === 'geojson') {
          this.mapObj.setFeatureState(
            { source: this.sourceId, id },
            { highlight: true }
          );
        } else {
          this.mapObj.setFeatureState(
            { source: this.sourceId, sourceLayer: this.sourceLayer, id },
            { highlight: true }
          );
        }
      }

      this.highlightSet.add(id);
    } else {
      throw new Error("incorrect input");
      
    }
  };

  addToHighlight = (ids: number[] | number) => {
    if (Utils.isArray(ids)) {
      for (const id of <number[]>ids) {
        if (this.mapObj.getSource(this.sourceId)) {
          if (this.mapObj.getSource(this.sourceId)?.type === 'geojson') {
            this.mapObj.setFeatureState(
              { source: this.sourceId, id },
              { highlight: true }
            );
          } else {
            this.mapObj.setFeatureState(
              {
                source: this.sourceId,
                sourceLayer: this.sourceLayer,
                id,
              },
              { highlight: true }
            );
          }
        }

        this.highlightSet.add(id);
      }
    } else if (!isNaN(<number>ids)) {
      const id = Number(ids);
      if (this.mapObj.getSource(this.sourceId)) {
        if (this.mapObj.getSource(this.sourceId)?.type === 'geojson') {
          this.mapObj.setFeatureState(
            { source: this.sourceId, id },
            { highlight: true }
          );
        } else {
          this.mapObj.setFeatureState(
            { source: this.sourceId, sourceLayer: this.sourceLayer, id },
            { highlight: true }
          );
        }
      }

      this.highlightSet.add(id);
    } else {
      // console.error("incorrect input");
    }
  };

  clearHighlight = () => {
    for (const id of this.highlightSet) {
      if (this.mapObj.getSource(this.sourceId)) {
        if (this.mapObj.getSource(this.sourceId)?.type === 'geojson') {
          this.mapObj.setFeatureState(
            { source: this.sourceId, id: <number>id },
            { highlight: false }
          );
        } else {
          this.mapObj.setFeatureState(
            {
              source: this.sourceId,
              sourceLayer: this.sourceLayer,
              id: <number>id,
            },
            { highlight: false }
          );
        }
      }
    }
    this.highlightSet = new Set();
  };

  removeFromHighlight = (ids: number[] | number) => {
    if (Utils.isArray(ids)) {
      for (const id of <number[]>ids) {
        if (this.mapObj.getSource(this.sourceId)) {
          if (this.mapObj.getSource(this.sourceId)?.type === 'geojson') {
            this.mapObj.setFeatureState(
              { source: this.sourceId, id },
              { highlight: false }
            );
          } else {
            this.mapObj.setFeatureState(
              {
                source: this.sourceId,
                sourceLayer: this.sourceLayer,
                id,
              },
              { highlight: false }
            );
          }
        }

        this.highlightSet.delete(id);
      }
    } else if (!isNaN(<number>ids)) {
      const id = Number(ids);
      if (this.mapObj.getSource(this.sourceId)) {
        if (this.mapObj.getSource(this.sourceId)?.type === 'geojson') {
          this.mapObj.setFeatureState(
            { source: this.sourceId, id },
            { highlight: false }
          );
        } else {
          this.mapObj.setFeatureState(
            { source: this.sourceId, sourceLayer: this.sourceLayer, id },
            { highlight: false }
          );
        }
      }

      this.highlightSet.delete(id);
    } else {
    }
  };

  select = (ids: number[] | number) => {
    this.clearSelection();

    if (Utils.isArray(ids)) {
      for (const id of <number[]>ids) {
        if (this.mapObj.getSource(this.sourceId)) {
          if (this.mapObj.getSource(this.sourceId)?.type === 'geojson') {
            this.mapObj.setFeatureState(
              { source: this.sourceId, id },
              { select: true }
            );
          } else {
            this.mapObj.setFeatureState(
              {
                source: this.sourceId,
                sourceLayer: this.sourceLayer,
                id,
              },
              { select: true }
            );
          }
        }

        this.selectionSet.add(id);
      }
    } else if (!isNaN(<number>ids)) {
      const id = Number(ids);
      if (this.mapObj.getSource(this.sourceId)) {
        if (this.mapObj.getSource(this.sourceId)?.type === 'geojson') {
          this.mapObj.setFeatureState(
            { source: this.sourceId, id },
            { select: true }
          );
        } else {
          this.mapObj.setFeatureState(
            { source: this.sourceId, sourceLayer: this.sourceLayer, id },
            { select: true }
          );
        }
      }

      this.selectionSet.add(id);
    } else {
      // console.error("incorrect input");
    }
  };

  clearSelection = () => {
    for (const id of this.selectionSet) {
      if (this.mapObj.getSource(this.sourceId)) {
        if (this.mapObj.getSource(this.sourceId)?.type === 'geojson') {
          this.mapObj.setFeatureState(
            { source: this.sourceId, id: <number>id },
            { select: false }
          );
        } else {
          this.mapObj.setFeatureState(
            {
              source: this.sourceId,
              sourceLayer: this.sourceLayer,
              id: <number>id,
            },
            { select: false }
          );
        }
      }
    }
    this.selectionSet = new Set();
  };

  addToSelecion = (ids: number[] | number) => {
    if (Utils.isArray(<number[]>ids)) {
      for (const id of <number[]>ids) {
        if (this.mapObj.getSource(this.sourceId)) {
          if (this.mapObj.getSource(this.sourceId)?.type === 'geojson') {
            this.mapObj.setFeatureState(
              { source: this.sourceId, id },
              { select: true }
            );
          } else {
            this.mapObj.setFeatureState(
              {
                source: this.sourceId,
                sourceLayer: this.sourceLayer,
                id,
              },
              { select: true }
            );
          }
        }

        this.selectionSet.add(id);
      }
    } else if (!isNaN(<number>ids)) {
      const id = Number(ids);
      if (this.mapObj.getSource(this.sourceId)) {
        if (this.mapObj.getSource(this.sourceId)?.type === 'geojson') {
          this.mapObj.setFeatureState(
            { source: this.sourceId, id },
            { select: true }
          );
        } else {
          this.mapObj.setFeatureState(
            { source: this.sourceId, sourceLayer: this.sourceLayer, id },
            { select: true }
          );
        }
      }

      this.selectionSet.add(id);
    } else {
      // console.error("incorrect input");
    }
  };

  removeFromSelection = (ids: number[] | number) => {
    if (Utils.isArray(ids)) {
      for (const id of <number[]>ids) {
        if (this.mapObj.getSource(this.sourceId)) {
          if (this.mapObj.getSource(this.sourceId)?.type === 'geojson') {
            this.mapObj.setFeatureState(
              { source: this.sourceId, id },
              { select: false }
            );
          } else {
            this.mapObj.setFeatureState(
              {
                source: this.sourceId,
                sourceLayer: this.sourceLayer,
                id,
              },
              { select: false }
            );
          }
        }

        this.selectionSet.delete(id);
      }
    } else if (!isNaN(<number>ids)) {
      const id = Number(ids);
      if (this.mapObj.getSource(this.sourceId)) {
        if (this.mapObj.getSource(this.sourceId)?.type === 'geojson') {
          this.mapObj.setFeatureState(
            { source: this.sourceId, id },
            { select: false }
          );
        } else {
          this.mapObj.setFeatureState(
            { source: this.sourceId, sourceLayer: this.sourceLayer, id },
            { select: false }
          );
        }
      }

      this.selectionSet.delete(id);
    } else {
      // console.error("incorrect input");
    }
  };

  addLabel = (
    field: string,
    option: layerObj = <layerObj>{}
  ) => {

    let parentFilter = this.getFilter()
    // let visibility = this.mapObj.getLayoutProperty(this.layerId, 'visibility');
    
    if (!field && this.publishedFields[0]) field = `{${this.publishedFields[0].name}}`;

    if (!field) field = "";

    if (this.mapObj.getLayer(`${this.layerId}$label`)) {
      this.mapObj.setLayoutProperty(`${this.layerId}$label`,'text-field', `${field}`)

      for (let key in option.paint) {
        this.mapObj.setPaintProperty(`${this.layerId}$label`,key, option.paint[key])
      }
  
      for (const key in option.layout) {
        this.mapObj.setLayoutProperty(`${this.layerId}$label`,key, option.layout[key])
      }
    }else {
      this.mapObj.addLayer(<LayerSpecification>{
        id: `${this.layerId}$label`,
        type: 'symbol',
        source: this.sourceId,
        'source-layer': this.sourceLayer,
        paint: option.paint || {
          'text-color': 'rgba(0, 0, 0, 1)',
          'text-halo-color': 'rgba(255, 255, 255, 1)',
          'text-halo-width': 2,
          'text-halo-blur': 0,
        },
        layout: option.layout || {
          'text-field': `${field}`,
          'text-allow-overlap': false,
          'text-font': ['Iran Sans Regular', 'Arial Unicode MS Regular'],
        },
      });
    }

    this._setLayerProperty('maxzoom',this.mapObj.getLayer(this.layerId)?.maxzoom ,'label');
    this._setLayerProperty('minzoom',this.mapObj.getLayer(this.layerId)?.minzoom ,'label');
  
    this.setSubLayerFilter(<FilterSpecification>parentFilter,'label');

      // let newOrder = this.mapObj.style._order.indexOf(this.layerId);
    this.mapObj.moveLayerOrder(`${this.layerId}$label`, this.layerId);

    this.mapObj.moveLayerOrder('geobox-gl-splash-circle');
      
    // if (visibility === 'none') {
    //   this.mapObj.setLayoutProperty(
    //     `${this.layerId}$symbol`,
    //     'visibility',
    //     'none'
    //   );
    // }
    
  };

  removeLabel = () => {
    if (this.mapObj.getLayer(`${this.layerId}$label`)) {
      // this._setLayerProperty('text-field', '', 'symbol');

      // let property = this._getLayerProperty('icon-image','symbol');
      // if (property?.basic === ''){
        this.mapObj.removeLayer(`${this.layerId}$label`);
      // }
    }
  };

  setLabelProperty = (type: property, value: string | number) => {
    if (!this.mapObj.getLayer(`${this.layerId}$label`)) {
      return
      // throw new Error("This layer has no label!");
    }
    this._setLayerProperty(type, value, 'label');
  };

  getLabelProperty = (type: property) => {
    if (!this.mapObj.getLayer(`${this.layerId}$label`)) {
      return
      // throw new Error("This layer has no label!");
    }
    return this._getLayerProperty(type, 'label');
  };

  addSymbol = (
    symbol: string = this.mapObj.symbolNames[0],
    option: layerObj = <layerObj>{}
  ) => {

    let parentFilter = this.getFilter();

    // let visibility = this.mapObj.getLayoutProperty(this.layerId, 'visibility');
    
    // if (this.mapObj.getLayer(this.layerId)?.type === "circle") this.mapObj.setLayoutProperty(this.layerId, 'visibility', 'none');
    
    if (this.mapObj.getLayer(`${this.layerId}$symbol`)) {
      this.mapObj.setLayoutProperty(`${this.layerId}$symbol`,'icon-image', symbol)

      for (let key in option.paint) {
        this.mapObj.setPaintProperty(`${this.layerId}$symbol`,key, option.paint[key])
      }
  
      for (const key in option.layout) {
        this.mapObj.setLayoutProperty(`${this.layerId}$symbol`,key, option.layout[key])
      }
    }else {
      const selectColor: string =
      this.mapObj.mapSettings.general_settings.selection_color; 

      const hoverColor: string =
        this.mapObj.mapSettings.general_settings.highlight_color;

      const paintObject = <paintObject>option.paint || <paintObject>{};

      // TODO this code is same and repeated for others. delete them.
      if (option.paint) {
        if (paintObject['icon-color']) {
          const [type, expression] = this.makeExpression(
            paintObject['icon-color']
          );

          const newExpression = <any[]>expression;

          if (type === 'interpolate') {
            for (let i = 4; i < newExpression.length; i += 2) {
              newExpression[i] = [
                'case',
                ['boolean', ['feature-state', 'select'], false],
                selectColor,
                ['boolean', ['feature-state', 'highlight'], false],
                hoverColor,
                newExpression[i],
              ];
            }

            paintObject['icon-color'] = <DataDrivenPropertyValueSpecification<string>>newExpression;
          } else {
            let selfColor = paintObject['icon-color'];
            if (type === 'identity') {
              selfColor = <DataDrivenPropertyValueSpecification<string>>(
                newExpression
              );
            }

            paintObject['icon-color'] = [
              'case',
              ['boolean', ['feature-state', 'select'], false],
              selectColor,
              ['boolean', ['feature-state', 'highlight'], false],
              hoverColor,
              <ExpressionInputType>selfColor,
            ];
          }
        }
      } else {
        paintObject['icon-color'] = [
          'case',
          ['boolean', ['feature-state', 'select'], false],
          selectColor,
          ['boolean', ['feature-state', 'highlight'], false],
          hoverColor,
          'rgba(15, 125, 77, 1)',
        ];
      }

      this.mapObj.addLayer(<LayerSpecification>{
        id: `${this.layerId}$symbol`,
        type: 'symbol',
        source: this.sourceId,
        'source-layer': this.sourceLayer,
        paint: paintObject,
        layout: option.layout || {
          'icon-image': symbol,
          'icon-allow-overlap': true,
          'text-font': ['Iran Sans Regular', 'Arial Unicode MS Regular'],
        },
      });
    }
   
    

    
    this._setLayerProperty('maxzoom',this.mapObj.getLayer(this.layerId)?.maxzoom ,'symbol');
    this._setLayerProperty('minzoom',this.mapObj.getLayer(this.layerId)?.minzoom ,'symbol');
    this.setSubLayerFilter(<FilterSpecification>parentFilter,'symbol');

    // let newOrder = this.mapObj.style._order.indexOf(this.layerId);
    this.mapObj.moveLayerOrder(`${this.layerId}$symbol`, this.layerId);

    this.mapObj.moveLayerOrder('geobox-gl-splash-circle');

    // if (visibility === 'none') {
    //   this.mapObj.setLayoutProperty(
    //     `${this.layerId}$symbol`,
    //     'visibility',
    //     'none'
    //   );
    // }
    
  };

  removeSymbol = () => {
    // const visibility = this.mapObj.getLayoutProperty(
    //   `${this.layerId}$symbol`,
    //   'visibility'
    // );

    // if (this.mapObj.getLayer(this.layerId)?.type === "circle" && visibility !== 'none'){
    //   this.mapObj.setLayoutProperty(this.layerId, 'visibility', 'visible');
    // }
    
    // if (this.mapObj.getLayer(`${this.layerId}$symbol`)) {
    //   this.mapObj.removeLayer(`${this.layerId}$symbol`);
    // }

    if (this.mapObj.getLayer(`${this.layerId}$symbol`)) {
      // this._setLayerProperty('icon-image', '', 'symbol');
      // let property = this._getLayerProperty('text-field','symbol');
      // if (property?.basic === ''){
        this.mapObj.removeLayer(`${this.layerId}$symbol`);
      // }
    }

  };

  setSymbolProperty = (type: property, value: string | number) => {
    if (!this.mapObj.getLayer(`${this.layerId}$symbol`)) {
      return;
      // throw new Error("This layer has no symbol!");
    }

    this._setLayerProperty(type, value, 'symbol');
  };

  getSymbolProperty = (type: property) => {
    if (!this.mapObj.getLayer(`${this.layerId}$symbol`)) {
      return;
      // throw new Error("This layer has no symbol!");
    }

    return this._getLayerProperty(type, 'symbol');
  };

  removeOutLine = () => {
    if (this.mapObj.getLayer(`${this.layerId}$outline`)) {
      this.mapObj.removeLayer(`${this.layerId}$outline`);
    }
  };

  setOutLineProperty = (type: property, value: string | number) => {
    if (!this.mapObj.getLayer(`${this.layerId}$outline`)) {
      return;
      // throw new Error("This layer has no outline!");
      
    }

    this._setLayerProperty(type, value, 'outline');
  };

  getOutLineProperty = (type: property) => {
    if (!this.mapObj.getLayer(`${this.layerId}$outline`)) {
      return;
      // throw new Error("This layer has no outline!");
      
    }

    return this._getLayerProperty(type, 'outline');

  };

  setFilter = (filter: FilterSpecification) => {
    this.mapObj.setFilter(this.layerId, filter);

    if (this.mapObj.getLayer(`${this.layerId}$symbol`)) {
      this.mapObj.setFilter(`${this.layerId}$symbol`, filter);
    }

    if (this.mapObj.getLayer(`${this.layerId}$label`)) {
      this.mapObj.setFilter(`${this.layerId}$label`, filter);
    }

    if (this.mapObj.getLayer(`${this.layerId}$outline`)) {
      this.mapObj.setFilter(`${this.layerId}$outline`, filter);
    }

    if (this.mapObj.getLayer(`${this.layerId}$extrusion`)) {
      this.mapObj.setFilter(`${this.layerId}$extrusion`, filter);
    }
  };

  getFilter = () => {
    return this.mapObj.getFilter(this.layerId);
  };

  removeFilter = () => {
    this.mapObj.setFilter(this.layerId, ['all']);

    if (this.mapObj.getLayer(`${this.layerId}$symbol`)) {
      this.mapObj.setFilter(`${this.layerId}$symbol`, ['all']);
    }

    if (this.mapObj.getLayer(`${this.layerId}$label`)) {
      this.mapObj.setFilter(`${this.layerId}$label`, ['all']);
    }

    if (this.mapObj.getLayer(`${this.layerId}$outline`)) {
      this.mapObj.setFilter(`${this.layerId}$outline`, ['all']);
    }

    if (this.mapObj.getLayer(`${this.layerId}$extrusion`)) {
      this.mapObj.setFilter(`${this.layerId}$extrusion`, ['all']);
    }
  };

  setSubLayerFilter = (
    filter: FilterSpecification,
    subLayerType: subLayerType
  ) => {
    if (subLayerType === 'main'){
      this.mapObj.setFilter(this.layerId, filter);
    }else {
      if (this.mapObj.getLayer(`${this.layerId}$${subLayerType}`)) {
        this.mapObj.setFilter(`${this.layerId}$${subLayerType}`, filter);
      }
    }
  };

  getSubLayerFilter = (
    subLayerType: subLayerType
  ) => {
    if (subLayerType === 'main'){
      return this.mapObj.getFilter(this.layerId);
    }else {
      if (this.mapObj.getLayer(`${this.layerId}$${subLayerType}`)) {
        return this.mapObj.getFilter(`${this.layerId}$${subLayerType}`);
      }
    } 
  };

  removeSubLayerFilter = (
    subLayerType: subLayerType
  ) => {
    if (subLayerType === 'main'){
      this.mapObj.setFilter(this.layerId, null);
    }else {
      if (this.mapObj.getLayer(`${this.layerId}$${subLayerType}`)) {
        this.mapObj.setFilter(`${this.layerId}$${subLayerType}`, null);
      }
    }
  }

  // TODO: set getSublayerproperty and _setLayerProperty method for extrusion, label and.... to reduce code
  // getSubLayerProperty = (
  //   type: property,
  //   subLayerType: subLayerType
  // ) => {
  //   let propertyValue;
  //   let paintProperty: any;
  //   try {
  //     if (type === 'maxzoom') {
  //       propertyValue = {"basic": this.mapObj.getLayer(`${this.layerId}$${subLayerType}`)?.maxzoom || defaults['maxzoom']};
  //     } else if (type === 'minzoom') {
  //       propertyValue = {"basic":this.mapObj.getLayer(`${this.layerId}$${subLayerType}`)?.minzoom || defaults['minzoom']};
  //     }else if (type === 'highlight-color') {
  //       paintProperty =(
  //         this.mapObj.getPaintProperty(this.layerId, <string>this.colorType)
  //       );
  //       if (paintProperty && paintProperty[0] === 'case') {
  //         if ((["string", "number", "boolean"].includes(typeof paintProperty[4]))) {
  //           propertyValue = {"basic": paintProperty[4]};
  //         }else {
  //           propertyValue = {"case": paintProperty[4]};
  //         }
  //       } else if (paintProperty && paintProperty[0] === 'match') {
  //         propertyValue = {"match": paintProperty};
  //       } else if (paintProperty && paintProperty[0] === 'interpolate') {
  //         propertyValue = {"interpolate": paintProperty[4][4]};
  //       }
  //     } else if (type === 'select-color') {
  //       paintProperty = (
  //         this.mapObj.getPaintProperty(this.layerId, <string>this.colorType)
  //       );
  //       if (paintProperty && paintProperty[0] === 'case') {
  //         if ((["string", "number", "boolean"].includes(typeof paintProperty[2]))) {
  //           propertyValue = {"basic": paintProperty[2]};
  //         }else {
  //           propertyValue = {"case": paintProperty[2]};
  //         }
  //       } else if (paintProperty && paintProperty[0] === 'match') {
  //         propertyValue = {"match": paintProperty};
  //       } else if (paintProperty && paintProperty[0] === 'interpolate') {
  //         propertyValue = {"interpolate": paintProperty[4][2]};
  //       }
      
  //     } else if (['line-color', 'fill-color', 'circle-color','icon-color','fill-extrusion-color'].includes(type)) {
  //       paintProperty = this.mapObj.getPaintProperty(`${this.layerId}$${subLayerType}`, type);
  //       let base = undefined;
  //       try {
  //         if (paintProperty && paintProperty.base){
  //           base = paintProperty.base;
  //           delete paintProperty.base;
  //         }
  //       } catch (error) {
  //         console.error(error)
  //       }

  //       if (typeof paintProperty !== "boolean" && !paintProperty){
  //         propertyValue = {"basic": defaults[type]};
  //       } else if (paintProperty && ((["string", "number", "boolean"].includes(typeof paintProperty)) )){
  //         propertyValue = {"basic": paintProperty};
  //       } else if (paintProperty && paintProperty[5][0] === 'get') {
  //         propertyValue = {"basic": paintProperty[5]};
  //       } else if (paintProperty && paintProperty[5][0] === 'match') {
  //         propertyValue = {"match": paintProperty[5]};
  //       } else if (paintProperty && paintProperty[0] === 'case') {
  //         if ( (["string", "number", "boolean"].includes(typeof paintProperty[5]))) {
  //           propertyValue = {"basic": paintProperty[5]};
  //         } else {
  //           try {
  //             propertyValue = {"case": paintProperty[5],"field":paintProperty[5][1][1][1]};
  //           } catch (error) {
  //             propertyValue = {"case": paintProperty[5]};
  //           }
  //         }
  //       } else if (paintProperty && paintProperty[0] === 'interpolate') {
  //         let propertyObject: expressionType = <expressionType>expression.parse(paintProperty);
         
  //         if (propertyObject?.inputs.length === 1){
  //           if ( (["string", "number", "boolean"].includes(typeof paintProperty[5])) ) {
  //             propertyValue = {"basic": paintProperty[4][3]};
  //           }else {
  //             try {
  //               propertyValue = {"case": paintProperty[4][3],"field":paintProperty[4][5][1][1][1]};
  //             } catch (error) {
  //               propertyValue = {"case": paintProperty[4][3]};
  //             }
              
  //           }
            
  //         }else {
  //           for (let index = 0; index < <number>propertyObject?.outputs.length; index++) {
  //             propertyObject.outputs[index] = propertyObject?.outputs[index][5];
  //             propertyObject.stops[index][1] = propertyObject.stops[index][1][5];
  //           }
  //           propertyValue = {"interpolate": propertyObject.stops, "field":propertyObject.fieldName};
  //           if (base){
  //             (<any>propertyValue)["base"] = base;
  //           }
  //         }
          
  //       }
  //     } else {
  //       let layerType: layerType = 'line';
  //       if (this.mapObj.getLayer(`${this.layerId}$${subLayerType}`))
  //         layerType = <layerType>this.mapObj.getLayer(`${this.layerId}$${subLayerType}`)?.type;

  //       let group = 'paint';
      
  //       if (layout[layerType].paint.includes(type)) {
  //         group = 'paint';
  //       } else {
  //         group = 'layout';
  //       }
      
  //       if (group === 'paint') {
  //         paintProperty = this.mapObj.getPaintProperty(`${this.layerId}$${subLayerType}`, type);
        
  //         let base = undefined;
  //         try {
  //           if (paintProperty && paintProperty.base){
  //             base = paintProperty.base;
  //             delete paintProperty.base;
  //           }
  //         } catch (error) {
  //           console.error(error)
  //         }

  //         if(typeof paintProperty !== "boolean" && !paintProperty){
  //           propertyValue = {"basic": (<any>defaults)[type]};
  //         } else if (paintProperty && ((["string", "number", "boolean"].includes(typeof paintProperty)))){
  //           propertyValue = {"basic": paintProperty};
  //         } else if (paintProperty && paintProperty[0] === 'get') {
  //           propertyValue = {"basic": paintProperty};
  //         } else if (paintProperty && paintProperty[0] === 'case') {
  //           // if (typeof paintProperty[5] == "string" || typeof paintProperty[5] == "number") {
  //           //   propertyValue = {"basic": paintProperty[5]};
  //           // }else {
  //             propertyValue = {"case": paintProperty};
  //           // }
            
  //         } else if (paintProperty && paintProperty[0] === 'match') {
  //             propertyValue = {"match": paintProperty};
  //         } else if (paintProperty && paintProperty[0] === 'interpolate') {
  //           let propertyObject: expressionType = <expressionType>expression.parse(paintProperty);
            
  //           if (propertyObject?.inputs.length === 1){
  //             if ((["string", "number", "boolean"].includes(typeof paintProperty[5]))) {
  //               propertyValue = {"basic": paintProperty[4][5]};
  //             }else {
  //               propertyValue = {"case": paintProperty[4][5]};
  //             }
              
  //           }else {
  //             for (let index = 0; index < <number>propertyObject?.outputs.length; index++) {
               
  //               propertyObject.outputs[index] = propertyObject?.outputs[index];
  //               propertyObject.stops[index][1] = propertyObject.stops[index][1];
  //             }
  //             propertyValue = {"interpolate": propertyObject.stops, "field":propertyObject.fieldName};
  //             if (base){
  //               (<any>propertyValue)["base"] = base;
  //             }
  //           }
            
  //         } else if (paintProperty && typeof paintProperty === 'object' && Object.keys(paintProperty)[0] === 'stops') {
        
  //           propertyValue = {"interpolate": paintProperty.stops, "field":"zoom"};
  //           if (base){
  //             (<any>propertyValue)["base"] = base;
  //           }
  //         } else {
  //           propertyValue = {"basic": paintProperty};
  //         }
  //       } else {
  //         paintProperty = this.mapObj.getLayoutProperty(`${this.layerId}$${subLayerType}`, type);
  //         let base = undefined;
  //         try {
  //           if (paintProperty && paintProperty.base){
  //             base = paintProperty.base;
  //             delete paintProperty.base;
  //           }
  //         } catch (error) {
  //           console.error(error)
  //         }
          

  //         if(typeof paintProperty !== "boolean" && !paintProperty){
  //           propertyValue = {"basic": (<any>defaults)[type]};
  //         } else if (paintProperty && ((["string", "number", "boolean"].includes(typeof paintProperty)))){
  //           propertyValue = {"basic": paintProperty};
  //         } else if (paintProperty && paintProperty[0] === 'get') {
  //           propertyValue = {"basic": paintProperty};
  //         } else if (paintProperty && paintProperty[0] === 'case') {
  //           if ((["string", "number", "boolean"].includes(typeof paintProperty[5]))) {
  //             propertyValue = {"basic": paintProperty};
  //           }else {
  //             propertyValue = {"case": paintProperty};
  //           }
            
  //         } else if (paintProperty && paintProperty[0] === 'match') {
  //           propertyValue = {"match": paintProperty};
  //         } else if (paintProperty && paintProperty[0] === 'interpolate') {
  //           let propertyObject: expressionType = <expressionType>expression.parse(paintProperty);
            
  //           if (propertyObject?.inputs.length === 1){
  //             if ((["string", "number", "boolean"].includes(typeof paintProperty))) {
  //               propertyValue = {"basic": paintProperty};
  //             }else {
  //               propertyValue = {"case": paintProperty};
  //             }
              
  //           }else {
  //             propertyValue = {"interpolate": propertyObject.stops, "field":propertyObject.fieldName};
  //             if (base){
  //               (<any>propertyValue)["base"] = base;
  //             }
  //           }
            
  //         }else if (paintProperty && typeof paintProperty === 'object' && Object.keys(paintProperty)[0] === 'stops') {
  //           propertyValue = {"interpolate": paintProperty.stops, "field":"zoom"};
  //           if (base){
  //             (<any>propertyValue)["base"] = base;
  //           }
  //         }else {
  //           propertyValue = {"basic": paintProperty};
  //         }
  //       }
  //     }
    
     
  //     return propertyValue;
  //   } catch (error) {
  //     console.error(error);
  //   }
  // };

  // setSubLayerProperty = (
  //   type: property,
  //   value: any,
  //   subLayerType:subLayerType,
  //   method: string = 'count'
  // ) => {

  //   try {
  //     if (type === 'maxzoom') {
  //       this.mapObj.setLayerZoomRange(
  //         `${this.layerId}$${subLayerType}`,
  //         this.mapObj.getLayer(`${this.layerId}$${subLayerType}`)?.minzoom || 0,
  //         <number>value
  //       );

  //       // if (subLayerType === 'cluster'){
  //       //   this.mapObj.setLayerZoomRange(
  //       //     `${this.layerId}$unclustered`,
  //       //     this.mapObj.getLayer(`${this.layerId}$${subLayerType}`)?.minzoom || 0,
  //       //     <number>value
  //       //   );

  //       //   this.mapObj.setLayerZoomRange(
  //       //     `${this.layerId}$cluster-count`,
  //       //     this.mapObj.getLayer(`${this.layerId}$${subLayerType}`)?.minzoom || 0,
  //       //     <number>value
  //       //   );
  //       // }

  //     } else if (type === 'minzoom') {
  //       this.mapObj.setLayerZoomRange(
  //         `${this.layerId}$${subLayerType}`,
  //         <number>value,
  //         this.mapObj.getLayer(`${this.layerId}$${subLayerType}`)?.maxzoom || 24
  //       );

  //       // if (subLayerType === 'cluster'){
  //       //   this.mapObj.setLayerZoomRange(
  //       //     `${this.layerId}$unclustered`,
  //       //     <number>value,
  //       //     this.mapObj.getLayer(`${this.layerId}$${subLayerType}`)?.maxzoom || 0
  //       //   );

  //       //   this.mapObj.setLayerZoomRange(
  //       //     `${this.layerId}$cluster-count`,
  //       //     <number>value,
  //       //     this.mapObj.getLayer(`${this.layerId}$${subLayerType}`)?.maxzoom || 0,
  //       //   );
  //       // }
  //     } else if (subLayerType === 'cluster' && (type === 'weight-field' || type === 'cluster-radius' || type === 'cluster-maxZoom')) {
  //       let style = this.mapObj.getStyle();
  //       let layerSource = <any> style.sources[`${this.sourceId}$_cluster`];

  //       if (type === 'weight-field'){
         
  
  //         if (method.toString().toLowerCase() === 'count'){
  //           layerSource["clusterProperties"]= {"cluster_value": ["+", 1]}

  //         } else {
  //           if (method.toString().toLowerCase() === 'avg'){
  //             layerSource["clusterProperties"]={
  //               "cluster_value": [
  //                   '/',
  //                   ['+', ['accumulated'], ['get', value]],
  //                   ['+', ['accumulated'], 1]
  //               ]
  //             }
  //           }else if (method.toString().toLowerCase() === 'max'){
  //             layerSource["clusterProperties"]={
  //               "cluster_value": ['max', ['get', value]]
  //             }
  //           }else if (method.toString().toLowerCase() === 'min'){
  //             layerSource["clusterProperties"]={
  //               "cluster_value": ['min', ['get', value]]
  //             }
  //           }else {
  //             layerSource["clusterProperties"]={
  //               "cluster_value": ["+", ["get", value]]
  //             }
  //           }
  //         }
    
  //       }else if (type === 'cluster-radius'){
  //         layerSource["clusterRadius"]= value;
  //       }else if (type === 'cluster-maxZoom'){
  //         layerSource["clusterMaxZoom"]= value;
  //       }
        
  //       style.sources[`${this.sourceId}$_cluster`] = <SourceSpecification>layerSource;
  
  //       this.mapObj.setStyle(style)

  //     }else {

  //       if ((subLayerType === 'outline' || subLayerType === 'symbol') && ['line-color','icon-color','circle-color'].includes(type)) {

  //         let paintProperty: Array<string | string[]> 

  //         paintProperty = value;

  //         paintProperty = this.prepareNewStyle(paintProperty,false, true);

  //         this.mapObj.setPaintProperty(
  //           `${this.layerId}$${subLayerType}`,
  //           type,
  //           paintProperty
  //         );

  //       }else if (['line-color','fill-color','fill-extrusion-color','icon-color','circle-color'].includes(type)) {
  //         let paintProperty: Array<string | string[]> 
         
  //         paintProperty = value;
  
  //         paintProperty = this.prepareNewStyle(paintProperty);

  //         this.mapObj.setPaintProperty(
  //           `${this.layerId}$${subLayerType}`,
  //           type,
  //           paintProperty
  //         );
  
  //       } else {
  //         let layerType: layerType = 'line';
  //         if (this.mapObj.getLayer(`${this.layerId}$${subLayerType}`))
  //           layerType = <layerType>(
  //             this.mapObj.getLayer(`${this.layerId}$${subLayerType}`)?.type
  //           );
  
  //         let group = 'paint';
  
  //         if (layout[layerType].paint.includes(type)) {
  //           group = 'paint';
  //         } else {
  //           group = 'layout';
  //         }
  
  //         if (layerType === 'symbol') {
  //           for (const field of this.allFields) {
  //             if (field.name === value) {
  //               value = `{${value}}`;
  //               break;
  //             }
  //           }
  //         }
  
         
  //         if (group === 'paint') {
  //           this.mapObj.setPaintProperty(
  //             `${this.layerId}$${subLayerType}`,
  //             type,
  //             value
  //           );
  //           // if (subLayerType === 'cluster'){
  //           //   this.mapObj.setPaintProperty(
  //           //     `${this.layerId}$unclustered`,
  //           //     type,
  //           //     value
  //           //   );

  //           //   this.mapObj.setPaintProperty(
  //           //     `${this.layerId}$cluster-count`,
  //           //     type,
  //           //     value
  //           //   );
  //           // }
  //         } else if (type === 'visibility') {
  //           this.mapObj.setLayoutProperty(
  //             `${this.layerId}$${subLayerType}`,
  //             type,
  //             value
  //           );

  //           // if (subLayerType === 'cluster'){
  //           //   this.mapObj.setLayoutProperty(
  //           //     `${this.layerId}$unclustered`,
  //           //     type,
  //           //     value
  //           //   );

  //           //   this.mapObj.setLayoutProperty(
  //           //     `${this.layerId}$cluster-count`,
  //           //     type,
  //           //     value
  //           //   );
  //           // }
  //         } else {
  //           this.mapObj.setLayoutProperty(`${this.layerId}$${subLayerType}`, type, value);
  //           // if (subLayerType === 'cluster'){
  //           //   this.mapObj.setLayoutProperty(
  //           //     `${this.layerId}$unclustered`,
  //           //     type,
  //           //     value
  //           //   );

  //           //   this.mapObj.setLayoutProperty(
  //           //     `${this.layerId}$cluster-count`,
  //           //     type,
  //           //     value
  //           //   );
  //           // }
  //         }
  //       }
          
  //     }
  //   } catch (error) {
  //     console.error(error);
  //   }
  // };

  flashFeature = async (id :number, centerPoint: any = [0,0], options: object = { maxZoom: 22 }) => {
    try {
      if (this.source["layer_type"] === "foreign") {
        if (this.mapObj.getSource(this.sourceId)) {
          if (
            this.mapObj.getSource(this.sourceId)?.type === "geojson"
          ) {
            this.mapObj.setFeatureState(
              { source: this.sourceId, id: id },
              { select: true }
            );
          } else {
            this.mapObj.setFeatureState(
              {
                source: this.sourceId,
                sourceLayer: this.sourceLayer,
                id: id,
              },
              { select: true }
            );
          }
        }

        try {
          this.mapObj.panTo(
            centerPoint,
            options
          );
        } catch (error) {
          // throw new Error("Feature not found");
        }

        const splashCircleSource = (<any>this.mapObj.getSource(
          "geobox-gl-splash-circle"
        ))._data;

        splashCircleSource.features = [
          {
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: [centerPoint[1], centerPoint[0]],
            },
          },
        ];

        (<any>this.mapObj.getSource("geobox-gl-splash-circle")).setData(
          splashCircleSource
        );

        this.mapObj.pulsingDot.start();

        setTimeout(() => {
          // Keep the select state if the feature is part of the current selection.
          const stillSelected = Array.from(this.selectionSet).some(
            (selectedId) => String(selectedId) === String(id)
          );

          if (!stillSelected && this.mapObj.getSource(this.sourceId)) {
            if (
              this.mapObj.getSource(this.sourceId)?.type === "geojson"
            ) {
              this.mapObj.setFeatureState(
                { source: this.sourceId, id: id },
                { select: false }
              );
            } else {
              this.mapObj.setFeatureState(
                {
                  source: this.sourceId,
                  sourceLayer: this.sourceLayer,
                  id: id,
                },
                { select: false }
              );
            }
          }

          this.mapObj.pulsingDot.stop();
        }, 1200);
      } else {
        const feature = await this.source.getFeatures({
          f: "json",
          q: `id=${id}`,
          out_srid: 4326,
          select_fields: ["id"],
        });

        if (feature.bbox.length === 0){
         throw new Error("Feature not found");
         
        }
        
        let featureBBox;
        try {
          featureBBox = feature.bbox;
          featureBBox = polygon([
            [
              [featureBBox[0], featureBBox[1]],
              [featureBBox[2], featureBBox[1]],
              [featureBBox[2], featureBBox[3]],
              [featureBBox[0], featureBBox[3]],
              [featureBBox[0], featureBBox[1]],
            ],
          ]);

          const featureCenterPoint = centroid(featureBBox);

          this.mapObj.panTo(
            <LngLatLike>featureCenterPoint.geometry.coordinates,
            options
          );
        } catch (error) {
          throw new Error("Feature not found");
        }

        const splashCircleSource: any = (<any>this.mapObj.getSource(
          "geobox-gl-splash-circle"
        ))._data;

        let flashList: Array<object> = [];

        if ( feature.features[0].geometry.type === 'MultiLineString'){
          
          feature.features[0].geometry.coordinates.forEach((coord: any) => {
            
            let pointsList = points(coord);

            var centerPoint = center(pointsList);

            flashList.push({
              "type": "Feature",
              "properties": {},
              "geometry": {
                "coordinates": centerPoint.geometry.coordinates,
                "type": "Point"
              }
            })

          });

          splashCircleSource.features = flashList;

        }else if (feature.features[0].geometry.type === 'MultiPolygon'){

          feature.features[0].geometry.coordinates.forEach((coord: any) => {
            let pointsList = points(coord[0]);

            var centerPoint = center(pointsList);
           
            flashList.push({
              "type": "Feature",
              "properties": {},
              "geometry": {
                "coordinates": centerPoint.geometry.coordinates,
                "type": "Point"
              }
            })

          });

          splashCircleSource.features = flashList;

        }else if (feature.features[0].geometry.type === 'MultiPoint'){

          feature.features[0].geometry.coordinates.forEach((coord: any) => {
            let pointsList = points([coord]);

            var centerPoint = center(pointsList);
         
            flashList.push({
              "type": "Feature",
              "properties": {},
              "geometry": {
                "coordinates": centerPoint.geometry.coordinates,
                "type": "Point"
              }
            })

          });

          splashCircleSource.features = flashList;

        }else {
          featureBBox = feature.bbox;
          featureBBox = polygon([
            [
              [featureBBox[0], featureBBox[1]],
              [featureBBox[2], featureBBox[1]],
              [featureBBox[2], featureBBox[3]],
              [featureBBox[0], featureBBox[3]],
              [featureBBox[0], featureBBox[1]],
            ],
          ]);

          centerPoint = centroid(featureBBox);
          
          splashCircleSource.features = [
            {
              type: "Feature",
              geometry: {
                type: "Point",
                coordinates: (centerPoint).geometry.coordinates,
              },
            },
          ];
        }

        if (this.mapObj.getSource(this.sourceId)) {
          if (
            this.mapObj.getSource(this.sourceId)?.type === "geojson"
          ) {
            this.mapObj.setFeatureState(
              { source: this.sourceId, id: id },
              { select: true }
            );
          } else {
            this.mapObj.setFeatureState(
              {
                source: this.sourceId,
                sourceLayer: this.sourceLayer,
                id: id,
              },
              { select: true }
            );
          }
        }

        (<any>this.mapObj.getSource("geobox-gl-splash-circle")).setData(
          splashCircleSource
        );

        this.mapObj.pulsingDot.start();

        
        setTimeout(() => {
          // Keep the select state if the feature is part of the current selection.
          const stillSelected = Array.from(this.selectionSet).some(
            (selectedId) => String(selectedId) === String(id)
          );

          if (!stillSelected && this.mapObj.getSource(this.sourceId)) {
            if (
              this.mapObj.getSource(this.sourceId)?.type === "geojson"
            ) {
              this.mapObj.setFeatureState(
                { source: this.sourceId, id: id },
                { select: false }
              );
            } else {
              this.mapObj.setFeatureState(
                {
                  source: this.sourceId,
                  sourceLayer: this.sourceLayer,
                  id: id,
                },
                { select: false }
              );
            }
          }

          this.mapObj.pulsingDot.stop();
        }, 1200);
        
      }
    } catch (error) {
      throw new Error(String(error));
    }
  };

  refreshLayer = async () => {
    await this.getPublishedFields();
    (<any>this.mapObj.getSource(this.sourceId)).setUrl();
  }

  changeSelectionColor = (selectionColor:string) => {
    const paintProperty:any = this.mapObj.getPaintProperty(
      this.layerId,
      <string>this.colorType
    );

    if (paintProperty[0] === "case") {
      paintProperty[2] = selectionColor;
    } else if (paintProperty[0] === "interpolate") {
      for (let i = 4; i < paintProperty.length; i += 2) {
        paintProperty[i][2] = selectionColor;
      }
    }

    this.mapObj.setPaintProperty(
      this.layerId,
      <string>this.colorType,
      paintProperty
    );

  };

  changeHighlightColor = (highlightColor:string) => {
    const paintProperty:any = this.mapObj.getPaintProperty(
      this.layerId,
      <string>this.colorType
    );

    if (paintProperty[0] === "case") {
      if (this.colorType !== "fill-color") paintProperty[4] = highlightColor;
    } else if (paintProperty[0] === "interpolate") {
      for (let i = 4; i < paintProperty.length; i += 2) {
        if (this.colorType !== "fill-color")
          paintProperty[i][4] = highlightColor;
      }
    }

    this.mapObj.setPaintProperty(
      this.layerId,
      <string>this.colorType,
      paintProperty
    );

  };

  duplicate = async (): Promise<FeatureLayer> => {

    const { PointVectorLayer } = await import('./PointVectorLayer')
    const { LineVectorLayer } = await import('./LineVectorLayer')
    const { PolygonVectorLayer } = await import('./PolygonVectorLayer')

    let style = this.mapObj.getStyle();

    let layerStyle = style.layers.find(layer => layer.id === this.layerId);

    let layerId = this.layerId;

    layerId = layerId.split('#')[0]
    
    let layerRepeatNumber = 1;

    let layerList = this.mapObj.getLayersOrder()
    for (const layer of style.layers) {
      // (this.layerId === layer.id) || 
      if (layerList.includes(`${layerId}#${layerRepeatNumber}`)) {
        layerRepeatNumber += 1;
      }
      
      // if (`${this.layerId} (copy ${layerRepeatNumber})` === layer.id) {
      //   layerRepeatNumber += 1;
      // }
    }

    // if (layerRepeatNumber > 0) {
      layerId = `${layerId}#${layerRepeatNumber}`;
    // }

    (<any>layerStyle).id = layerId;

    let featureLayer;

    switch (this.layerType) {
    case 'Point':
    case 'MultiPoint':
      featureLayer = new PointVectorLayer(this.source,  this.mapObj, this.sourceLayer, layerId);
      break;
    case 'Polyline':
      featureLayer = new LineVectorLayer(this.source,  this.mapObj, this.sourceLayer, layerId);
      break;
    case 'Polygon':
      featureLayer = new PolygonVectorLayer(this.source,  this.mapObj, this.sourceLayer, layerId);
      break;

    default:
      featureLayer = new FeatureLayer(this.source,  this.mapObj, this.sourceLayer, layerId);
      break;
  }
    
 
    if (this.sourceId.includes('geobox://tileset/')) {
      (<any>layerStyle)['source-layer-id'] = this.source.id;
    }

    this.mapObj.originalStyle.layers.push(<LayerSpecification>layerStyle)
   
    this.mapObj.addLayer(<LayerSpecification>layerStyle);

    this.mapObj.libreMapFeatureLayers[layerId] = <FeatureLayer>featureLayer;

    // let newOrder = this.mapObj.style._order.indexOf(this.layerId);

    this.mapObj.moveLayerOrder(layerId, this.layerId);

    if (this.mapObj.getLayer(`${this.layerId}$label`)) {
      layerStyle = style.layers.find(layer => layer.id === `${this.layerId}$label`);

      layerId = `${layerId}$label`;
  
      (<any>layerStyle).id = layerId;

      this.mapObj.addLayer(<LayerSpecification>layerStyle);
      this.mapObj.moveLayerOrder(layerId, `${this.layerId}$label`);
    }

    if (this.mapObj.getLayer(`${this.layerId}$symbol`)) {
      layerStyle = style.layers.find(layer => layer.id === `${this.layerId}$symbol`);

      layerId = `${layerId}$symbol`;
  
      (<any>layerStyle).id = layerId;

      this.mapObj.addLayer(<LayerSpecification>layerStyle);
      this.mapObj.moveLayerOrder(layerId, `${this.layerId}$symbol`);
    }

    if (this.mapObj.getLayer(`${this.layerId}$outline`)) {
      layerStyle = style.layers.find(layer => layer.id === `${this.layerId}$outline`);

      layerId = `${layerId}$outline`;
  
      (<any>layerStyle).id = layerId;

      this.mapObj.addLayer(<LayerSpecification>layerStyle);
      this.mapObj.moveLayerOrder(layerId, `${this.layerId}$outline`);
    }

    if (this.mapObj.getLayer(`${this.layerId}$extrusion`)) {
      layerStyle = style.layers.find(layer => layer.id === `${this.layerId}$extrusion`);

      layerId = `${layerId}$extrusion`;
  
      (<any>layerStyle).id = layerId;

      this.mapObj.addLayer(<LayerSpecification>layerStyle);
      this.mapObj.moveLayerOrder(layerId, `${this.layerId}$extrusion`);
    }

    if (this.mapObj.getLayer(`${this.layerId}$heatmap`)) {
      layerStyle = style.layers.find(layer => layer.id === `${this.layerId}$heatmap`);

      layerId = `${layerId}$heatmap`;
  
      (<any>layerStyle).id = layerId;

      this.mapObj.addLayer(<LayerSpecification>layerStyle);
      this.mapObj.moveLayerOrder(layerId, `${this.layerId}$heatmap`);
    }

    if (this.mapObj.getLayer(`${this.layerId}$cluster`)) {
      layerStyle = style.layers.find(layer => layer.id === `${this.layerId}$cluster`);

      layerId = `${layerId}$cluster`;
  
      (<any>layerStyle).id = layerId;

      this.mapObj.addLayer(<LayerSpecification>layerStyle);
      this.mapObj.moveLayerOrder(layerId, `${this.layerId}$cluster`);

      layerStyle = style.layers.find(layer => layer.id === `${this.layerId}$cluster-count`);

      layerId = `${layerId}$cluster-count`;
  
      (<any>layerStyle).id = layerId;

      this.mapObj.addLayer(<LayerSpecification>layerStyle);
      this.mapObj.moveLayerOrder(layerId, `${layerId}$cluster-count`);

      layerStyle = style.layers.find(layer => layer.id === `${this.layerId}$unclustered`);

      layerId = `${layerId}$unclustered`;
  
      (<any>layerStyle).id = layerId;

      this.mapObj.addLayer(<LayerSpecification>layerStyle);
      this.mapObj.moveLayerOrder(layerId, `${layerId}$unclustered`);
    }

    this.mapObj.moveLayerOrder('geobox-gl-splash-circle');
    
    return featureLayer;
  }

  drawNewFeature = async (
    subtype?:
      | 'polygon'
      | 'rectangle'
      | 'circle'
      | 'freehand'
      | 'line'
      | 'bezier'
      | 'point'
  ) => {
    if (this.mapObj.loaded()) {

      if (this.mapObj.selectByLocationMode) {
        throw new Error("Select By LocationMode mode is active!");
        
      }

      // if (this.mapObj.drawMode) {
      //   throw new Error("Draw mode is active!");
      // }

      // try {
      //   this.mapObj.disableDraw();
      // } catch (error) {
      // } 

      const _this = this;

      if (this.mapObj.drawTools && this.mapObj.drawMode && ['draw_line_string','draw_polygon', 'draw_point'].includes(this.mapObj.drawTools.getMode())){
        throw new Error("Draw new feature is active!");
      }

      if (!this.mapObj._controls.includes(this.mapObj.drawTools)) {
        this.mapObj.addControl(this.mapObj.drawTools);

        try {
          const drawTools= document.getElementsByClassName("mapbox-gl-draw_line")[0];
          if (drawTools && drawTools.parentElement instanceof HTMLElement) {
            drawTools.parentElement.style.display = "none";
          }

          const snapTools= document.getElementsByClassName("mapbox-gl-draw_line")[0];
          if (snapTools && snapTools.parentElement instanceof HTMLElement) {
            snapTools.parentElement.style.display = "none";
          }

        } catch (error) {}
      }

      this.mapObj.drawMode = true;

      // this.on('draw.create', this._updateMeasurements);
      // this.on('draw.update', this._updateMeasurements);

      const drawMode = resolveDrawModeForLayer(this.source.layer_type, subtype);
      this.mapObj.drawTools.changeMode(drawMode as any);

      this.mapObj.refreshEditGeometryHostUi?.();

      return this.mapObj.drawTools;
    }
    return new Promise((resolve) => {
      setTimeout(async () => {
        const result = await this.drawNewFeature(subtype);
        resolve(result);
      }, 100);
    });
  }

  createFeatureFromSketch = async (
    sketch: { type?: string; geometry?: unknown; properties?: object },
    apiSrid: number = GEOBOX_API_GEOMETRY_SRID
  ) => {
    const prepared = prepareSketchFeatureForCreate(
      sketch as import('geojson').Feature,
      this.source.layer_type,
      Boolean(this.source.has_z),
      apiSrid
    );
    return this.source.createFeature(prepared);
  };

  updateFeatureFromSketch = async (
    featureId: string | number,
    sketch: { type?: string; geometry?: unknown; properties?: object },
    apiSrid: number = GEOBOX_API_GEOMETRY_SRID
  ) => {
    const prepared = prepareSketchFeatureForCreate(
      sketch as import('geojson').Feature,
      this.source.layer_type,
      Boolean(this.source.has_z),
      apiSrid
    );
    return this.source.updateFeature(featureId, prepared);
  };

  cancelDrawNewFeature = () =>{
    if (!this.mapObj.drawTools) return;

    if (!this.mapObj.drawMode) {
      try {
        const count = this.mapObj.drawTools.getAll?.()?.features?.length ?? 0;
        if (count > 0) {
          this.mapObj.disableDraw();
        }
      } catch {
        /* ignore */
      }
      return;
    }

    const drawModeId = this.mapObj.drawTools.getMode();

    if (
      ['draw_line_string', 'draw_polygon', 'draw_point'].includes(drawModeId)
    ) {
      let length = this.mapObj.drawTools.getAll().features.length;

      if (length > 0) {
        this.mapObj.drawTools.delete(<string>this.mapObj.drawTools.getAll().features[length - 1].id);
        this.mapObj.drawTools.changeMode('simple_select');
      }

      length = this.mapObj.drawTools.getAll().features.length;
      if (this.mapObj.drawTools && length === 0) {
        this.mapObj.disableDraw();
      }
    } else {
      // After e.g. draw_point the mode is often simple_select while drawMode is still true — still cancel the session
      this.mapObj.disableDraw();
    }
  }

  editFeature = async (id:number) => {
    if (this.mapObj.loaded()) {

      if (this.mapObj.selectByLocationMode) {
        throw new Error("Select By LocationMode mode is active!");
      }

      if (this.mapObj.measurementMode) {
        throw new Error("Measurement mode is active!");
      }

      // if (this.mapObj.drawMode) {
      //   console.error("Draw mode is active!", "error");
      //   return;
      // }

      if (this._isFeatureBeingEdited(id)) {
        // Feature already on the draw canvas — re-focus transform handles instead of failing.
        const drawTools = this.mapObj.drawTools;
        const drawId = this._resolveDrawFeatureId(id);
        if (drawId && drawTools) {
          this.mapObj.drawMode = true;
          drawTools.changeMode(drawTools.modes.DIRECT_SELECT, { featureId: drawId });
          this.mapObj.refreshEditGeometryHostUi?.();
        }
        return;
      }

      // try {
      //   this.mapObj.disableDraw();
      // } catch (error) {
      // } 

      const _this = this;

      if (!this.layer_settings.edit_settings.editable) {
        throw new Error(`${this.layerId} is not editable`);
      } 
  
      let featureHasGeometry = this.layer_settings.edit_settings.edit_geometry;
  
      let features = await this.source.getFeatures({
        f: "json",
        q: `id=${Utils.escapeHTML(id)}`,
        out_srid: 4326,
        skip_geometry: !featureHasGeometry
      });
  
      let currentFeature = features.features[0];
   
      if (!currentFeature){
        throw new Error('Feature not found');
      }
      
      this.editIds.push(id);

      // this._geoJson = this._currentFeature;
  
      if (featureHasGeometry){
  
        if (!this.mapObj._controls.includes(this.mapObj.drawTools)){
          this.mapObj.addControl(this.mapObj.drawTools);
        }
        

        try {
          const drawTools= document.getElementsByClassName("mapbox-gl-draw_line")[0];
          if (drawTools && drawTools.parentElement instanceof HTMLElement) {
            drawTools.parentElement.style.display = "none";
          }

          const snapTools= document.getElementsByClassName("mapbox-gl-draw_line")[0];
          if (snapTools && snapTools.parentElement instanceof HTMLElement) {
            snapTools.parentElement.style.display = "none";
          }

        } catch (error) {}
        
        this.mapObj.drawMode = true;
  
        // this.mapObj.drawTools.deleteAll();
        
        let updatedFilter = ["!=", "$id", id]
  
        let currentFilter = this.mapObj.getFilter(this.layerId);
  
        if (currentFilter){
          updatedFilter = 
          [
            "all",
            <any>currentFilter,
            ["!=", "$id",  id]
          ];
        }
  
        this.mapObj.setFilter(this.layerId, <FilterSpecification>updatedFilter);

        const serverId = String(currentFeature.id ?? id);
        const drawFeature = featureGeometryForDraw(
          currentFeature as import('geojson').Feature,
          this.source.layer_type
        );
        drawFeature.id = serverId;
        drawFeature.properties = {
          ...(drawFeature.properties ?? {}),
          id: currentFeature.id ?? id,
        };
        const featureDrawId = this.mapObj.drawTools.add(drawFeature);
        const drawId = String(featureDrawId[0] ?? serverId);
        const geomType = String(currentFeature.geometry?.type ?? '');
        if (geomType === 'Point' || geomType === 'MultiPoint') {
          this.mapObj.drawTools.changeMode(this.mapObj.drawTools.modes.DIRECT_SELECT, {
            featureId: drawId,
          });
        } else {
          this.mapObj.drawTools.changeMode(this.mapObj.drawTools.modes.DIRECT_SELECT, {
            featureId: drawId,
          });
        }
  
        if (currentFeature.geometry.type === "MultiPoint") {
          this.mapObj.drawTools.uncombineFeatures();
        }

        this.mapObj.refreshEditGeometryHostUi?.();
        setTimeout(() => this.mapObj.refreshEditGeometryHostUi?.(), 0);
      }

      // this.on('draw.create', this._updateMeasurements);
      // this.on('draw.update', this._updateMeasurements);

      

      //draw.changeMode('draw_line_string')
      // this.mapObj.drawTools.changeMode('draw_polygon')

      // switch (this.source.layer_type) {
      //   case "Polyline":
      //     this.mapObj.drawTools.changeMode(
      //       this.mapObj.drawTools.modes.DRAW_LINE_STRING
      //     );
      //     break;
      //   case "Polygon":
      //     this.mapObj.drawTools.changeMode(this.mapObj.drawTools.modes.DRAW_POLYGON);
      //     break;
      //   default:
      //     this.mapObj.drawTools.changeMode(this.mapObj.drawTools.modes.DRAW_POINT);
      //     break;
      // }

      return this.mapObj.drawTools;
    }

    return new Promise((resolve) => {
      setTimeout(async () => {
        const result = await this.editFeature(id);
        resolve(result);
      }, 100);
    });
  }

  getEdittingFeature = (id:number | string) =>{
    if (this.mapObj._controls.includes(this.mapObj.drawTools)){
      return this.mapObj.drawTools.get(String(id));
    }else{
      throw new Error("Feature not found");
    }
  }

  setEdittingFeatureProperty = (id:number | string, property:string, value: string) => {

    this.mapObj.drawTools.setFeatureProperty(String(id),property,value);

  }

  saveFeature = async (id:number, properties:object = {}) => {

    if (id !== 0 && !id){
      throw new Error("Feature not found");
    }

    let featureHasGeometry = this.layer_settings.edit_settings.edit_geometry;

    const drawId = this._resolveDrawFeatureId(id);
    let draw = drawId ? this.mapObj.drawTools.get(drawId) : undefined;

    let geometry_type;

    switch (this.source.layer_type) {
      case "Polyline":
        geometry_type = "MultiLineString";
        break;
      case "Polygon":
        geometry_type = "MultiPolygon";
        break;
      case "Point":
        geometry_type = "Point";
        break;

      default:
        geometry_type = "MultiPoint";
        break;
    }

    let currentGeoJson = {
      type: "Feature",
      geometry: {
        type: geometry_type,
        coordinates: [],
      },
      properties: properties
    };

    if (featureHasGeometry && draw?.geometry) {
      const prepared = prepareSketchFeatureForCreate(
        { type: 'Feature', geometry: draw.geometry, properties },
        this.source.layer_type,
        Boolean(this.source.has_z),
        GEOBOX_API_GEOMETRY_SRID
      );
      currentGeoJson.geometry = prepared.geometry as typeof currentGeoJson.geometry;
    }

    const allFields = [];
    for (const field of this.allFields) {
      if (field.hyperlink) continue;
      allFields.push(field)
    }
    var dateFormat = this.mapObj.mapSettings["general_settings"]["calendar_type"] || "default";
    
    // if (id !== 0 && !id) {
    //   this._geoJson.geometry.coordinates = mercatorCoordinates;

    //   const fieldsAndValues = {};

    //   if (table) {
    //     var rowLength = table.rows.length;
    //     for (var i = 2; i < rowLength; i++) {
    //       var rowCells = table.rows.item(i).cells;
    //       if (rowCells.item(1).children[0].value === "") continue;
    //       fieldsAndValues[allFields[i-2].name] =
    //         rowCells.item(1).children[0].value;
    //     }

    //     if (Object.keys(fieldsAndValues).length > 0) {
    //       this._geoJson["properties"] = fieldsAndValues;
    //     }
    //   }
      
    //   feature = await this._source.createFeature(this._geoJson);
    // } else {
      let feature
      try {
        if (isNaN(id)){
          feature = await this.source.createFeature(currentGeoJson);
        }else {
          feature = await this.source.updateFeature(id, currentGeoJson);
        }

      } catch (error) {
        throw new Error(String(error));
        
        // this._mapObj._showInfo(error, "error");
        // feature = this._currentFeature;
      }

      // let featureFilter = ["!=", "$id", id]
      let currentFilter = <any>this.mapObj.getFilter(this.layerId);
      let updatedFilter;

      if (!currentFilter || (currentFilter && currentFilter[0] === '!=' && currentFilter[1] === '$id' && currentFilter[2] === id )){
        updatedFilter = null;
      }else if (currentFilter) {
        let index = 0
        for (let param of <any>currentFilter){
          if (typeof(param) == 'object' && param[0] === '!=' && param[1] === '$id' && param[2] === id){
            break;
          }
          index +=1;
        }
        if (index > 0) {
          (<any>currentFilter).splice(index, 1);
        }
        // updatedFilter = currentFilter[1] || ["all"];
      }

      this._deleteDrawFeature(id);

      this.mapObj.setFilter(this.layerId, updatedFilter);

      (<Source>this.mapObj.getSource(this.sourceId)).setUrl();

      this._removeEditId(id);

      return feature
      // const exists = mainArray.some(arr =>
      //   arr.length === targetArray.length &&
      //   arr.every((val, index) => val === targetArray[index])
      // );
      

    // }
  }
  

  cancelEdit = (id:number) =>{
    this._removeEditId(id);

    let currentFilter = this.mapObj.getFilter(this.layerId);
    let updatedFilter;
    let index = -1;

    if (Array.isArray((<any>currentFilter)[1])){
      var hash:any = {};
      for(var i = 0 ; i < (<any>currentFilter).length; i += 1) {
          hash[(<any>currentFilter)[i]] = i;
      }
      
      var idFilter:keyof typeof hash = <keyof typeof hash><unknown>['!=', '$id', id];

      if(hash.hasOwnProperty(idFilter)) {
        index = hash[idFilter];
      }
  
      if (index > -1) {
        (<any>currentFilter).splice(index, 1);
      }
    }
   
    if (!currentFilter || (currentFilter && (<any>currentFilter)[0] === '!=' && (<any>currentFilter)[1] === '$id' )){
      updatedFilter = null;
    }else if (currentFilter) {
      updatedFilter = (<any>currentFilter)[1] || ["all"];
    }

    this.mapObj.setFilter(this.layerId, updatedFilter);
    this._deleteDrawFeature(id);

    // this._mapObj.removeEditTools();
    // this._mapObj._drawMode = false;
    // this._mapObj._editMode = false;
  }

 
  
  // _prepareEditFeature = async (id:number) => {
  //   // readOnly = false;
  //   if (!this.layer_settings.edit_settings.editable) {
  //     throw new Error("`${this._layerId} is not editable`");
  //   } 

  //   let featureHasGeometry = this.layer_settings.edit_settings.edit_geometry;

  //   // this.mapObj._updateSnapLayers();

  //   // let coordinate_edit_btn = this._editSideBar.querySelector("#geobox-gl-current-feature-edit-geometry_parameters-btn");
  //   // let geometry_parameters_edit_btn = this._editSideBar.querySelector("#geobox-gl-current-feature-edit-geometry_parameters-btn");
  //   // let cancel_edit_btn = this._editSideBar.querySelector("#geobox-gl-cancel-edit-current-feature");
  //   // let save_edit_btn = this._editSideBar.querySelector("#geobox-gl-save-edit-current-feature");
  //   // let delete_feature_btn = this._editSideBar.querySelector("#geobox-gl-delete-current-feature");
  //   // let edit_feature_btn = this._editSideBar.querySelector("#geobox-gl-edit-current-feature");
  
  //   // if (featureHasGeometry){
  //   //   coordinate_edit_btn.style.display = 'block';
  //   //   geometry_parameters_edit_btn.style.display = 'block';
  //   // }
  //   // delete_feature_btn.style.display = 'none';
  //   // edit_feature_btn.style.display = 'none';
  //   // cancel_edit_btn.style.display = 'block';
  //   // save_edit_btn.style.display = 'block';

  //   // this._mapObj._identifyMode = false;
  //   // this.mapObj._editMode = true; 
  //   // this._mapObj._mainDOM.querySelector("#geobox-gl-feature-status-info").innerText = 'edit';
    
  //   // const table = this._mapObj._mainDOM.querySelector('#geobox-gl-feature-attribute-table');
  //   // if (table) {
  //   //   var dateFormat = this._mapObj._mapSettings["general_settings"]["calendar_type"] || "default";

     

  //   //   var totalLength = table.childNodes[1].childNodes.length;
  //   //   if (this._layerType === "Polyline"){
  //   //     totalLength = table.childNodes[1].childNodes.length - 1;
  //   //   }else if (this._layerType === "Polygon"){
  //   //     totalLength = table.childNodes[1].childNodes.length - 2;
  //   //   }

  //   //   const allFields = [];
  //   //   for (const field of this._allFields) {
  //   //     if (field.hyperlink) continue;
  //   //     allFields.push(field)
  //   //   }

  //   //   let fieldReadOnly = false;

  //   //   for (let index = 1; index < totalLength; index++) {

  //   //     if (this._layer_settings.edit_settings.editable_attributes.includes(allFields[index-1].name) || this._layer_settings.edit_settings.editable_attributes === '[ALL]' ){
  //   //       fieldReadOnly = false
  //   //     }else{
  //   //       fieldReadOnly = true
  //   //     }
        
  //   //     var row = table.childNodes[1].childNodes[index].childNodes[1].childNodes[0];
  
  //   //     if (!fieldReadOnly) row.removeAttribute('readonly');
  //   //     if (row.nodeName === 'SELECT' && !fieldReadOnly) row.disabled = false;
  //   //     if (dateFormat === 'shamsi' && (row.getAttribute('datatype') === 'Date' || row.getAttribute('datatype') ===  'datetime-local')) row.setAttribute('data-jdp','');
  //   //   }
      
  //   // }

  //   let features = await this.source.getFeatures({
  //     f: "json",
  //     q: `id=${Utils.escapeHTML(id)}`,
  //     out_srid: 3857,
  //     skip_geometry: !featureHasGeometry
  //   });

  //   let currentFeature = features.features[0];
 
  //   if (!currentFeature){
  //     throw new Error('Feature not found');
  //   }
    
  //   // this._geoJson = this._currentFeature;

  //   if (featureHasGeometry){

  //     this.mapObj.addEditTools(currentFeature.geometry.type);

  //     this.mapObj.draw.deleteAll();
      
  //     let updatedFilter = ["!=", "$id", this._currentID]

  //     let currentFilter = this._mapObj.LibreMap.getFilter(this._layerId);

  //     if (currentFilter){
  //       updatedFilter = 
  //       [
  //         "all",
  //         currentFilter,
  //         ["!=", "$id",  this._currentID]
  //       ];
  //     }

  //     this._mapObj.LibreMap.setFilter(this._layerId, updatedFilter);

  //     this._featureDrawId = this._mapObj.draw.add(this._currentFeature);

  //     if (this._currentFeature.geometry.type !== "Point") {
  //       this._mapObj.draw.changeMode(this._mapObj.draw.modes.DIRECT_SELECT, {
  //         featureId: this._featureDrawId[0],
  //       });
  //     }

  //     if (this._currentFeature.geometry.type === "MultiPoint") {
  //       this._mapObj.draw.uncombineFeatures();
  //     }
  //   }
  // }

}
